<template>
  <v-container>
    <!-- Header -->
    <v-row
      class="mb-2"
      align="center"
    >
      <v-col>
        <v-btn
          text
          @click="$router.push('/eod/ldb-reconciliation')"
        >
          <v-icon left>
            mdi-arrow-left
          </v-icon>
          {{ t.backToBatchList }}
        </v-btn>
        <span
          v-if="batch.date_from"
          class="ml-4 text-h6 font-weight-bold"
        >
          {{ moment(batch.date_from).format('DD-MM-YYYY') }}
          <span v-if="batch.date_to && batch.date_to !== batch.date_from">
            — {{ moment(batch.date_to).format('DD-MM-YYYY') }}
          </span>
        </span>
        <v-chip
          v-if="batch.status"
          small
          label
          :color="batch.status === 'closed' ? 'green' : 'amber darken-1'"
          dark
          class="ml-3 font-weight-bold"
        >
          {{ batch.status === 'closed' ? t.closed : t.open }}
        </v-chip>
      </v-col>
      <v-col class="text-right d-flex align-center justify-end">
        <v-btn-toggle
          v-model="lang"
          mandatory
          dense
          class="mr-4"
        >
          <v-btn
            x-small
            value="lo"
          >
            ລາວ
          </v-btn>
          <v-btn
            x-small
            value="en"
          >
            EN
          </v-btn>
        </v-btn-toggle>
        <v-btn
          color="green"
          dark
          @click="exportBatch()"
        >
          <v-icon left>
            mdi-download
          </v-icon>
          {{ t.export }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Sub-Reconciliation Section (New Workflow) -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-bank-check</v-icon>
        ກວດສອບຕາມບັນຊີ
        <v-spacer />
        <v-btn
          v-if="allSubsComplete && subReconciliations.length > 0"
          color="primary"
          small
          depressed
          :loading="generatingSummary"
          @click="generateEodSummary"
        >
          <v-icon small left>mdi-chart-box</v-icon>
          ສ້າງ EOD Summary
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            v-for="sub in subReconciliations"
            :key="sub.id"
            cols="12"
            sm="4"
          >
            <sub-reconciliation-card
              :sub="sub"
              :batch-id="$route.params.batchId"
              @uploaded="onSubUploaded"
              @complete="completeSubReconciliation"
              @reopen="reopenSubReconciliation"
              @view-detail="viewSubDetail"
            />
          </v-col>
          <v-col
            v-for="acc in availableAccounts"
            :key="'add-' + acc.id"
            cols="12"
            sm="4"
          >
            <v-card class="rounded-lg" outlined style="border-style: dashed;">
              <v-card-text class="text-center pa-6">
                <v-btn
                  color="primary"
                  text
                  @click="createSubReconciliation(acc.id)"
                >
                  <v-icon left>mdi-plus</v-icon>
                  ເພີ່ມ {{ acc.label }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- EOD Summary Panel -->
    <eod-summary-panel
      v-if="eodSummary"
      :batch-id="$route.params.batchId"
      :summary="eodSummary"
      :sub-reconciliations="subReconciliations"
      class="mb-4"
      @close-eod="closeEod"
      @proposed="fetchBatchData"
    />

    <!-- Legacy Upload Section (for old batches without sub-reconciliations) -->
    <v-card v-if="subReconciliations.length === 0" class="mb-4">
      <v-card-title>{{ t.uploadStatement }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-file-input
              v-model="bcelFile"
              accept=".xlsx"
              label="BCEL Statement (.xlsx)"
              outlined
              dense
              :hint="t.maxFileSize"
              persistent-hint
            />
            <v-btn
              small
              color="#49A3DA"
              dark
              :loading="uploadingBcel"
              :disabled="!bcelFile"
              @click="uploadStatement('BCEL')"
            >
              Upload BCEL
            </v-btn>
            <div v-if="stmtSummary.BCEL && stmtSummary.BCEL.uploaded" class="mt-2">
              <v-chip small label color="green" dark class="mr-1">
                <v-icon x-small left>mdi-check</v-icon>
                {{ t.uploaded }}
              </v-chip>
              <span class="text-caption font-weight-bold">{{ stmtSummary.BCEL.file_name }}</span>
              <div class="text-caption grey--text mt-1">
                {{ Intl.NumberFormat().format(stmtSummary.BCEL.total_rows_in_file || 0) }} {{ t.rowsInFile }},
                {{ Intl.NumberFormat().format(stmtSummary.BCEL.total_rows || 0) }} {{ t.rowsInRange }}
              </div>
              <div
                v-if="stmtSummary.BCEL.total_rows_in_file > 0 && stmtSummary.BCEL.total_rows == 0"
                class="text-caption orange--text mt-1"
              >
                <v-icon x-small color="orange" class="mr-1">mdi-alert</v-icon>
                {{ t.noRowsMatchDate }}
              </div>
            </div>
          </v-col>
          <v-col cols="6">
            <v-file-input
              v-model="ldbFile"
              accept=".xlsx,.pdf"
              label="LDB Statement (.xlsx, .pdf)"
              outlined
              dense
              :hint="t.maxFileSize"
              persistent-hint
            />
            <v-btn
              small
              color="#49A3DA"
              dark
              :loading="uploadingLdb"
              :disabled="!ldbFile"
              @click="uploadStatement('LDB')"
            >
              Upload LDB
            </v-btn>
            <div v-if="stmtSummary.LDB && stmtSummary.LDB.uploaded" class="mt-2">
              <v-chip small label color="green" dark class="mr-1">
                <v-icon x-small left>mdi-check</v-icon>
                {{ t.uploaded }}
              </v-chip>
              <span class="text-caption font-weight-bold">{{ stmtSummary.LDB.file_name }}</span>
              <div class="text-caption grey--text mt-1">
                {{ Intl.NumberFormat().format(stmtSummary.LDB.total_rows_in_file || 0) }} {{ t.rowsInFile }},
                {{ Intl.NumberFormat().format(stmtSummary.LDB.total_rows || 0) }} {{ t.rowsInRange }}
              </div>
              <div
                v-if="stmtSummary.LDB.total_rows_in_file > 0 && stmtSummary.LDB.total_rows == 0"
                class="text-caption orange--text mt-1"
              >
                <v-icon x-small color="orange" class="mr-1">mdi-alert</v-icon>
                {{ t.noRowsMatchDate }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Statement Summary Cards (shown when statements uploaded) -->
    <div v-show="hasStatements">
      <v-row class="mb-4">
        <v-col
          v-for="card in stmtSummaryCards"
          :key="card.key"
          cols="12"
          sm="6"
          md
        >
          <v-card
            :color="card.color"
            dark
            class="pa-4 cursor-pointer summary-card"
            :class="{ 'elevation-6': stmtMatchFilter === card.key, 'elevation-1': stmtMatchFilter !== card.key }"
            @click="filterByStmtCard(card.key)"
          >
            <div class="text-h3 font-weight-bold">
              {{ Intl.NumberFormat().format(card.count) }}
            </div>
            <div class="text-subtitle-1 font-weight-bold mt-1">
              {{ card.label }}
            </div>
            <div class="text-h6 font-weight-bold mt-1">
              {{ fmtLAK(card.amount) }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Per-Bank Summary with Breakdown -->
      <v-row class="mb-4">
        <v-col
          v-for="bank in ['BCEL', 'LDB']"
          :key="bank"
          cols="12"
          md="6"
        >
          <v-card
            outlined
            class="pa-3"
            :class="{ 'elevation-3': bankFilter === bank }"
          >
            <div class="text-subtitle-1 font-weight-bold mb-2">
              <v-icon
                small
                :color="bank === 'BCEL' ? 'blue' : 'purple'"
                class="mr-1"
              >
                mdi-bank
              </v-icon>
              {{ bank }}
            </div>
            <v-simple-table
              v-if="stmtSummary[bank]"
              dense
              class="summary-table"
            >
              <thead>
                <tr>
                  <th>{{ t.category }}</th>
                  <th class="text-right">{{ t.qty }}</th>
                  <th class="text-right">{{ t.amountLak }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="font-weight-bold">{{ t.totalStatement }}</td>
                  <td class="text-right font-weight-bold">{{ stmtSummary[bank].total_rows || 0 }}</td>
                  <td class="text-right font-weight-bold">{{ fmtLAK(stmtSummary[bank].total_amount) }}</td>
                </tr>
                <tr class="row-green">
                  <td><v-icon x-small color="green" class="mr-1">mdi-check-circle</v-icon>{{ t.matched }}</td>
                  <td class="text-right">{{ stmtSummary[bank].matched_count || 0 }}</td>
                  <td class="text-right">{{ fmtLAK(stmtSummary[bank].matched_amount) }}</td>
                </tr>
                <tr class="row-red">
                  <td><v-icon x-small color="red" class="mr-1">mdi-alert-circle</v-icon>{{ t.missedPayments }}</td>
                  <td class="text-right">{{ stmtSummary[bank].missed_payment_count || 0 }}</td>
                  <td class="text-right">{{ fmtLAK(stmtSummary[bank].missed_payment_amount) }}</td>
                </tr>
                <tr class="row-orange">
                  <td><v-icon x-small color="orange" class="mr-1">mdi-help-circle</v-icon>{{ t.notInSystem }}</td>
                  <td class="text-right">{{ stmtSummary[bank].not_in_system_count || 0 }}</td>
                  <td class="text-right">{{ fmtLAK(stmtSummary[bank].not_in_system_amount) }}</td>
                </tr>
                <tr class="row-amber">
                  <td><v-icon x-small color="amber darken-3" class="mr-1">mdi-swap-horizontal</v-icon>{{ t.mismatch }}</td>
                  <td class="text-right">{{ stmtSummary[bank].amount_mismatch_count || 0 }}</td>
                  <td class="text-right">{{ fmtLAK(stmtSummary[bank].amount_mismatch_amount) }}</td>
                </tr>
                <tr class="row-grey">
                  <td><v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon>{{ t.expired }}</td>
                  <td class="text-right">{{ stmtSummary[bank].expired_count || 0 }}</td>
                  <td class="text-right">{{ fmtLAK(stmtSummary[bank].expired_amount) }}</td>
                </tr>
                <tr class="row-blue">
                  <td><v-icon x-small color="blue" class="mr-1">mdi-cash-check</v-icon>{{ t.paidNotInStatement }}</td>
                  <td class="text-right">{{ stmtSummary[bank].paid_not_in_statement_count || 0 }}</td>
                  <td class="text-right">{{ fmtLAK(stmtSummary[bank].paid_not_in_statement_amount) }}</td>
                </tr>
                <tr>
                  <td><v-icon x-small color="orange" class="mr-1">mdi-timer-sand</v-icon>{{ t.pending }}</td>
                  <td class="text-right">{{ stmtSummary[bank].pending_count || 0 }}</td>
                  <td class="text-right">—</td>
                </tr>
                <tr class="row-green">
                  <td><v-icon x-small color="green" class="mr-1">mdi-check-decagram</v-icon>{{ t.approved }}</td>
                  <td class="text-right">{{ stmtSummary[bank].approved_count || 0 }}</td>
                  <td class="text-right">{{ fmtLAK(stmtSummary[bank].approved_amount) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div
              v-else
              class="grey--text text-center pa-4"
            >
              {{ t.notUploaded }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Reconciliation Settlement -->
      <v-card
        class="mb-4"
        elevation="2"
        style="border-left: 4px solid #49A3DA;"
      >
        <v-card-title class="py-3 settlement-header">
          <v-icon
            color="#49A3DA"
            class="mr-2"
          >
            mdi-scale-balance
          </v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ t.reconciliationSettlement }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <!-- Settlement Table: Combined + Per-Bank -->
          <v-simple-table dense class="settlement-detail-table">
            <thead>
              <tr class="settlement-th-row">
                <th class="text-left" style="width: 30%;">{{ t.settlementItem }}</th>
                <th class="text-right" style="width: 23%;"><v-chip x-small label color="blue" dark>BCEL</v-chip></th>
                <th class="text-right" style="width: 23%;"><v-chip x-small label color="purple" dark>LDB</v-chip></th>
                <th class="text-right font-weight-bold" style="width: 24%;">{{ t.total }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- Bank Statement Total -->
              <tr class="settlement-row-bank">
                <td class="font-weight-bold">
                  <v-icon small color="blue darken-1" class="mr-1">mdi-bank</v-icon>
                  {{ t.bankStatementTotal }}
                </td>
                <td class="text-right font-weight-bold blue--text text--darken-2">{{ fmtLAK(stmtSummary.BCEL ? stmtSummary.BCEL.total_amount : 0) }}</td>
                <td class="text-right font-weight-bold purple--text text--darken-1">{{ fmtLAK(stmtSummary.LDB ? stmtSummary.LDB.total_amount : 0) }}</td>
                <td class="text-right font-weight-bold" style="font-size: 15px !important;">{{ fmtLAK(totalBankAmount) }}</td>
              </tr>
              <!-- System Confirmed -->
              <tr class="settlement-row-confirmed">
                <td class="font-weight-bold">
                  <v-icon small color="green darken-1" class="mr-1">mdi-check-decagram</v-icon>
                  {{ t.systemConfirmed }}
                </td>
                <td class="text-right font-weight-bold green--text text--darken-2">{{ fmtLAK(bankConfirmed('BCEL')) }}</td>
                <td class="text-right font-weight-bold green--text text--darken-2">{{ fmtLAK(bankConfirmed('LDB')) }}</td>
                <td class="text-right font-weight-bold" style="font-size: 15px !important;">{{ fmtLAK(totalSystemConfirmed) }}</td>
              </tr>
              <!-- Matched sub-row -->
              <tr>
                <td class="pl-8 text-caption">
                  <v-icon x-small color="green" class="mr-1">mdi-check-circle</v-icon>
                  {{ t.matched }}
                </td>
                <td class="text-right text-caption">{{ fmtLAK(stmtSummary.BCEL ? stmtSummary.BCEL.matched_amount : 0) }}</td>
                <td class="text-right text-caption">{{ fmtLAK(stmtSummary.LDB ? stmtSummary.LDB.matched_amount : 0) }}</td>
                <td class="text-right text-caption">{{ fmtLAK(combinedSummary.matched_amount) }}</td>
              </tr>
              <!-- Approved sub-row -->
              <tr>
                <td class="pl-8 text-caption">
                  <v-icon x-small color="green" class="mr-1">mdi-check-decagram</v-icon>
                  {{ t.approved }}
                </td>
                <td class="text-right text-caption">{{ fmtLAK(stmtSummary.BCEL ? stmtSummary.BCEL.approved_amount : 0) }}</td>
                <td class="text-right text-caption">{{ fmtLAK(stmtSummary.LDB ? stmtSummary.LDB.approved_amount : 0) }}</td>
                <td class="text-right text-caption">{{ fmtLAK(Number(stmtSummary.BCEL ? stmtSummary.BCEL.approved_amount || 0 : 0) + Number(stmtSummary.LDB ? stmtSummary.LDB.approved_amount || 0 : 0)) }}</td>
              </tr>
              <!-- Difference -->
              <tr :class="settlementDiff === 0 ? 'settlement-row-diff-ok' : 'settlement-row-diff-err'">
                <td class="font-weight-bold">
                  <v-icon
                    small
                    :color="settlementDiff === 0 ? 'green' : 'red'"
                    class="mr-1"
                  >
                    {{ settlementDiff === 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                  </v-icon>
                  {{ t.difference }}
                </td>
                <td
                  class="text-right font-weight-bold"
                  :class="bankDiff('BCEL') === 0 ? 'green--text' : 'red--text'"
                >
                  {{ fmtLAK(bankDiff('BCEL')) }}
                </td>
                <td
                  class="text-right font-weight-bold"
                  :class="bankDiff('LDB') === 0 ? 'green--text' : 'red--text'"
                >
                  {{ fmtLAK(bankDiff('LDB')) }}
                </td>
                <td
                  class="text-right font-weight-bold"
                  :class="settlementDiff === 0 ? 'green--text' : 'red--text'"
                  style="font-size: 16px !important;"
                >
                  {{ fmtLAK(settlementDiff) }}
                </td>
              </tr>
              <!-- Pending -->
              <tr v-if="totalPending > 0">
                <td class="text-caption orange--text" colspan="4">
                  <v-icon x-small color="orange" class="mr-1">mdi-timer-sand</v-icon>
                  {{ t.pendingReview }}: {{ totalPending }} — {{ t.resolveToClose }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <!-- Settlement Actions -->
        <v-divider class="my-3" />

        <!-- Status: OPEN (also while sub-reconciliations run and after the EOD summary) -->
        <div v-if="['open', 'reconciling', 'summary_ready'].includes(batch.status)">
          <v-alert
            v-if="batch.close_rejection_reason"
            type="error"
            dense
            outlined
            class="mb-3"
          >
            {{ t.closeRejected }}: {{ batch.close_rejection_reason }}
          </v-alert>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="actualReceived"
                :label="t.actualReceived"
                outlined
                class="settlement-input"
                suffix="LAK"
                @input="onActualInput($event)"
              />
            </v-col>
            <v-col cols="12" sm="5">
              <v-textarea
                v-model="settlementComment"
                :label="t.comment"
                outlined
                class="settlement-input"
                rows="1"
                auto-grow
              />
            </v-col>
            <v-col cols="12" sm="3" class="d-flex align-start flex-wrap">
              <v-btn
                small
                color="#49A3DA"
                dark
                class="mr-2 mb-1"
                :loading="savingSettlement"
                @click="saveSettlement()"
              >
                <v-icon small left>mdi-content-save</v-icon>
                {{ t.save }}
              </v-btn>
              <v-btn
                v-if="settlementDiff === 0 || settlementDiff === '-'"
                small
                color="green"
                dark
                class="mb-1"
                :loading="closingBatch"
                @click="confirmCloseEod()"
              >
                <v-icon small left>mdi-lock</v-icon>
                {{ t.closeEod }}
              </v-btn>
              <v-btn
                v-else
                small
                color="orange darken-1"
                dark
                class="mb-1"
                :loading="proposingClose"
                @click="showProposeDialog = true"
              >
                <v-icon small left>mdi-send</v-icon>
                {{ t.proposeClose }}
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Status: PENDING_APPROVAL -->
        <div v-else-if="batch.status === 'pending_approval'">
          <v-alert
            type="warning"
            dense
            prominent
            class="mb-3"
          >
            <div class="font-weight-bold">{{ t.pendingApproval }}</div>
            <div class="text-body-2 mt-1">
              {{ t.proposedBy }}: {{ batch.close_proposed_by ? batch.close_proposed_by.name : '-' }}
              | {{ batch.close_proposed_at ? moment(batch.close_proposed_at).format("DD-MM-YYYY HH:mm") : '' }}
            </div>
            <div v-if="batch.close_proposal_comment" class="text-body-2 mt-1">
              {{ t.comment }}: {{ batch.close_proposal_comment }}
            </div>
            <div class="text-caption mt-1">{{ t.threePeopleRule }}</div>
          </v-alert>

          <v-row dense>
            <!-- IT Manager -->
            <v-col cols="12" sm="6">
              <v-card outlined class="pa-3">
                <div class="text-subtitle-2 font-weight-bold mb-2">
                  <v-icon small class="mr-1">mdi-monitor</v-icon>
                  {{ t.itManager }}
                </div>
                <div v-if="batch.it_manager_approval === 'approved'" class="green--text">
                  <v-icon small color="green" class="mr-1">mdi-check-circle</v-icon>
                  {{ t.approvedStatus }} — {{ batch.it_manager_approved_by ? batch.it_manager_approved_by.name : '' }}
                  <span class="text-caption ml-1">{{ batch.it_manager_approved_at ? moment(batch.it_manager_approved_at).format("DD-MM HH:mm") : '' }}</span>
                </div>
                <div v-else-if="batch.it_manager_approval === 'rejected'" class="red--text">
                  <v-icon small color="red" class="mr-1">mdi-close-circle</v-icon>
                  {{ t.rejectedStatus }}
                </div>
                <div v-else>
                  <v-chip small label color="orange" dark class="mr-2">{{ t.pendingStatus }}</v-chip>
                  <span v-if="$can('eod-approve-close-it')">
                    <v-btn v-if="canApproveCloseAs('it_manager')" x-small color="green" dark class="mr-1" :loading="approvingIt" @click="approveClose('it_manager')">
                      {{ t.approve }}
                    </v-btn>
                    <v-btn x-small color="red" dark @click="rejectRole = 'it_manager'; showRejectCloseDialog = true">
                      {{ t.reject }}
                    </v-btn>
                  </span>
                </div>
              </v-card>
            </v-col>
            <!-- Finance Manager -->
            <v-col cols="12" sm="6">
              <v-card outlined class="pa-3">
                <div class="text-subtitle-2 font-weight-bold mb-2">
                  <v-icon small class="mr-1">mdi-cash-multiple</v-icon>
                  {{ t.financeManager }}
                </div>
                <div v-if="batch.finance_manager_approval === 'approved'" class="green--text">
                  <v-icon small color="green" class="mr-1">mdi-check-circle</v-icon>
                  {{ t.approvedStatus }} — {{ batch.finance_manager_approved_by ? batch.finance_manager_approved_by.name : '' }}
                  <span class="text-caption ml-1">{{ batch.finance_manager_approved_at ? moment(batch.finance_manager_approved_at).format("DD-MM HH:mm") : '' }}</span>
                </div>
                <div v-else-if="batch.finance_manager_approval === 'rejected'" class="red--text">
                  <v-icon small color="red" class="mr-1">mdi-close-circle</v-icon>
                  {{ t.rejectedStatus }}
                </div>
                <div v-else>
                  <v-chip small label color="orange" dark class="mr-2">{{ t.pendingStatus }}</v-chip>
                  <span v-if="$can('eod-approve-close-finance')">
                    <v-btn v-if="canApproveCloseAs('finance_manager')" x-small color="green" dark class="mr-1" :loading="approvingFinance" @click="approveClose('finance_manager')">
                      {{ t.approve }}
                    </v-btn>
                    <v-btn x-small color="red" dark @click="rejectRole = 'finance_manager'; showRejectCloseDialog = true">
                      {{ t.reject }}
                    </v-btn>
                  </span>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Status: CLOSED -->
        <div v-else-if="batch.status === 'closed'" class="text-caption grey--text mt-2">
          <v-icon x-small class="mr-1">mdi-lock</v-icon>
          {{ t.closedBy }}: {{ batch.closed_by ? batch.closed_by.name : '-' }}
          | {{ batch.closed_at ? moment(batch.closed_at).format("DD-MM-YYYY HH:mm") : '' }}
        </div>
      </v-card>

      <!-- Close EOD Confirmation (diff=0) -->
      <v-dialog v-model="showCloseEodDialog" max-width="450">
        <v-card>
          <v-card-title>{{ t.confirmCloseTitle }}</v-card-title>
          <v-card-text>{{ t.confirmCloseMsg }}</v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="showCloseEodDialog = false">{{ t.cancel }}</v-btn>
            <v-btn color="green" dark :loading="closingBatch" @click="doCloseEod()">
              {{ t.closeEod }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Propose Close Dialog (diff≠0) -->
      <v-dialog v-model="showProposeDialog" max-width="500">
        <v-card>
          <v-card-title>{{ t.proposeClose }}</v-card-title>
          <v-card-text>
            <p>{{ t.proposeCloseMsg }}</p>
            <v-text-field
              :value="fmtLAK(settlementDiff)"
              :label="t.difference"
              outlined
              dense
              readonly
              filled
            />
            <v-textarea
              v-model="proposeComment"
              :label="t.proposeReason"
              outlined
              dense
              rows="3"
              :hint="t.proposeReasonHint"
              persistent-hint
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="showProposeDialog = false">{{ t.cancel }}</v-btn>
            <v-btn
              color="orange darken-1"
              dark
              :loading="proposingClose"
              :disabled="!proposeComment.trim()"
              @click="doProposeClose()"
            >
              {{ t.proposeClose }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reject Close Dialog -->
      <v-dialog v-model="showRejectCloseDialog" max-width="500">
        <v-card>
          <v-card-title>{{ t.rejectClose }}</v-card-title>
          <v-card-text>
            <v-textarea
              v-model="rejectCloseReason"
              :label="t.rejectionReason"
              outlined
              dense
              rows="3"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="showRejectCloseDialog = false">{{ t.cancel }}</v-btn>
            <v-btn
              color="red"
              dark
              :disabled="!rejectCloseReason.trim()"
              @click="doRejectClose()"
            >
              {{ t.reject }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bank Filter Tabs -->
      <v-btn-toggle
        v-model="bankFilter"
        mandatory
        dense
        class="mb-4"
      >
        <v-btn value="BCEL">
          BCEL
        </v-btn>
        <v-btn value="LDB">
          LDB
        </v-btn>
      </v-btn-toggle>

      <!-- Filters Row -->
      <v-card class="mb-4">
        <v-card-text class="pb-0">
          <v-row
            dense
            align="center"
          >
            <v-col
              cols="12"
              sm="3"
            >
              <v-select
                v-model="stmtMatchFilter"
                :items="stmtMatchFilterOptions"
                :label="t.matchStatus"
                outlined
                dense
              />
            </v-col>
            <v-col
              cols="12"
              sm="3"
            >
              <v-select
                v-model="stmtResolutionFilter"
                :items="stmtResolutionFilterOptions"
                :label="t.resolution"
                outlined
                dense
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-text-field
                v-model="stmtSearch"
                :label="t.search"
                prepend-inner-icon="mdi-magnify"
                clearable
                dense
                outlined
              />
            </v-col>
            <v-col
              cols="12"
              sm="2"
              class="text-right"
            >
              <v-btn
                small
                color="#49A3DA"
                dark
                @click="fetchStatementRecords()"
              >
                <v-icon
                  small
                  left
                >
                  mdi-filter
                </v-icon>
                {{ t.applyFilter }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Bulk Action Bar -->
        <v-toolbar
          v-if="selectedStmtItems.length > 0"
          flat
          dense
          color="grey lighten-4"
        >
          <span class="mr-4 font-weight-bold">{{ selectedStmtItems.length }} {{ t.selected }}</span>
          <v-btn
            small
            color="green"
            dark
            class="mr-2"
            @click="confirmBulkApproveStmt()"
          >
            {{ t.approveSelected }}
          </v-btn>
          <v-btn
            small
            color="red"
            dark
            class="mr-2"
            @click="startBulkRejectStmt()"
          >
            {{ t.rejectSelected }}
          </v-btn>
          <v-btn
            v-if="qrSearchableStmtItems.length > 0"
            small
            color="teal"
            dark
            class="mr-2"
            :loading="enquiringBulk"
            @click="bulkEnquireQr()"
          >
            <v-icon small left>mdi-magnify</v-icon>
            {{ t.searchQr }}
          </v-btn>
          <v-btn
            small
            color="indigo"
            dark
            :loading="findingCustomerBulk"
            @click="bulkFindCustomer()"
          >
            <v-icon small left>mdi-account-search</v-icon>
            {{ t.findCustomer }}
          </v-btn>
        </v-toolbar>

        <!-- Statement Data Table -->
        <v-card-text class="pt-0">
          <v-data-table
            v-model="selectedStmtItems"
            :headers="stmtHeaders"
            :items="stmtRecords"
            :show-select="hasPendingStmtItems"
            :items-per-page="perPage"
            :footer-props="footerProps"
            item-key="id"
            class="eod-table"
            :item-class="rowClass"
          >
            <template v-slot:item.data-table-select="{ item, isSelected, select }">
              <v-simple-checkbox
                v-if="item.resolution_status === 'pending'"
                :value="isSelected"
                @input="select($event)"
              />
            </template>

            <template v-slot:item.statement_date="{ item }">
              {{ item.statement_date ? moment(item.statement_date).format("DD-MM-YYYY") : '-' }}
            </template>

            <template v-slot:item.transaction_time="{ item }">
              {{ formatTransactionTime(item.transaction_time) }}
            </template>

            <template v-slot:item.billing_id="{ item }">
              {{ formatBillingId(item.billing_id) }}
            </template>

            <template v-slot:item.bt_date="{ item }">
              {{ item.bt_date ? moment(item.bt_date).format("DD-MM-YYYY") : '-' }}
            </template>

            <template v-slot:item.customer_phone="{ item }">
              {{ item.customer_phone || item.phone || '-' }}
            </template>

            <template v-slot:item.customer_name="{ item }">
              {{ item.customer_name || '-' }}
              <v-chip
                v-if="item.match_basis && item.resolution_status === 'pending'"
                x-small
                label
                outlined
                :color="item.match_basis === 'customer_id' ? 'green' : 'blue'"
                class="ml-1"
              >
                {{ matchBasisLabel(item.match_basis) }}
              </v-chip>
            </template>

            <template v-slot:item.amount="{ item }">
              <span class="font-weight-bold">{{ fmtLAK(item.amount) }}</span>
            </template>

            <template v-slot:item.bt_amount="{ item }">
              <span :class="{ 'red--text font-weight-bold': item.bt_amount != null && item.bt_amount !== item.amount }">
                {{ item.bt_amount != null ? fmtLAK(item.bt_amount) : '-' }}
              </span>
            </template>

            <template v-slot:item.payment_type="{ item }">
              <v-chip
                v-if="item.payment_type"
                small
                label
                :color="paymentTypeColor(item.payment_type)"
                dark
                class="font-weight-bold"
                :title="item.payment_type"
              >
                {{ paymentTypeLabel(item.payment_type, item.bank) }}
              </v-chip>
              <span v-else>-</span>
            </template>

          </v-data-table>
        </v-card-text>
      </v-card>
    </div>

    <!-- QR Match Results Modal -->
    <v-dialog
      v-model="showQrMatchModal"
      max-width="900"
      scrollable
    >
      <v-card>
        <v-card-title class="py-3">
          <v-icon color="teal" class="mr-2">mdi-magnify</v-icon>
          {{ t.qrMatchResults }}
          <v-spacer />
          <v-btn icon @click="showQrMatchModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 500px;">
          <div
            v-for="(result, idx) in qrMatchResults"
            :key="idx"
            class="mb-4"
          >
            <!-- Statement record info -->
            <div class="text-subtitle-2 font-weight-bold mb-2 pa-2 bg-light-grey">
              <v-chip x-small label color="grey" dark class="mr-2">{{ result.transaction_ref || result.ref }}</v-chip>
              {{ fmtLAK(result.amount) }}
              <span v-if="result.customer_name" class="ml-2 grey--text">{{ result.customer_name }}</span>
            </div>
            <!-- Matches -->
            <div v-if="result.matches && result.matches.length > 0">
              <v-card
                v-for="match in result.matches"
                :key="match.qr_generator_id"
                outlined
                class="mb-2 pa-3"
                :style="match.ldb_inquiry && match.ldb_inquiry.status === 'FNLD' ? 'border-left: 3px solid #4caf50;' : ''"
              >
                <v-row dense align="center">
                  <v-col cols="8">
                    <div class="mb-1">
                      <v-chip x-small label color="indigo" dark class="mr-1">{{ match.bill_number }}</v-chip>
                      <v-chip
                        v-if="match.ldb_inquiry"
                        x-small
                        label
                        :color="match.ldb_inquiry.status === 'FNLD' ? 'green' : 'grey'"
                        dark
                        class="mr-1"
                      >
                        {{ match.ldb_inquiry.status }}
                      </v-chip>
                      <v-chip x-small label outlined class="mr-1">{{ match.status }}</v-chip>
                    </div>
                    <div class="text-body-2 font-weight-bold">
                      {{ match.owner ? match.owner.name : '-' }}
                      <span v-if="match.owner && match.owner.phone" class="grey--text ml-2">{{ match.owner.phone }}</span>
                    </div>
                    <div class="text-caption grey--text">
                      {{ t.qrAmount }}: {{ fmtLAK(match.amount) }}
                      <span v-if="match.ldb_inquiry">
                        | {{ t.paymentDate }}: {{ match.ldb_inquiry.payment_at ? moment(match.ldb_inquiry.payment_at).format('DD-MM-YYYY HH:mm') : '-' }}
                        <span v-if="match.ldb_inquiry.payer_name"> | {{ match.ldb_inquiry.payer_name }}</span>
                      </span>
                    </div>
                    <div v-if="match.billing_ids && match.billing_ids.length" class="text-caption mt-1">
                      <v-icon x-small class="mr-1">mdi-receipt</v-icon>
                      {{ t.billings }}: {{ match.billing_ids.join(', ') }}
                    </div>
                  </v-col>
                  <v-col cols="4" class="text-right">
                    <v-btn
                      small
                      color="green"
                      dark
                      :loading="linkingId === match.qr_generator_id"
                      @click="linkStatementQr(result.statement_record_id || enquiringItem.id, match.qr_generator_id)"
                    >
                      <v-icon small left>mdi-link</v-icon>
                      {{ t.linkQr }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </div>
            <div v-else class="text-center pa-3 grey--text">
              <v-icon color="grey">mdi-magnify-close</v-icon>
              {{ t.noQrMatch }}
            </div>
          </div>
          <div v-if="qrMatchResults.length === 0" class="text-center pa-4 grey--text">
            <v-progress-circular v-if="enquiringBulk" indeterminate color="teal" class="mr-2" />
            {{ enquiringBulk ? t.searching : t.noQrMatch }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Find Customer Modal -->
    <v-dialog
      v-model="showFindCustomerModal"
      max-width="900"
      scrollable
    >
      <v-card>
        <v-card-title class="py-3">
          <v-icon color="indigo" class="mr-2">mdi-account-search</v-icon>
          {{ t.findCustomerResults }}
          <v-spacer />
          <v-btn icon @click="showFindCustomerModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 600px;">
          <!-- Search fields -->
          <div
            v-if="findCustomerItem && !findingCustomerBulk"
            class="mb-3 pa-3 bg-light-grey" style="border-radius: 8px;"
          >
            <div class="text-subtitle-2 font-weight-bold mb-2">
              {{ findCustomerItem.transaction_ref }} — {{ fmtLAK(findCustomerItem.amount) }}
              <span v-if="findCustomerItem.description" class="grey--text ml-2 text-caption">{{ findCustomerItem.description }}</span>
            </div>
            <v-row dense>
              <v-col cols="4">
                <v-text-field
                  v-model="findPhone"
                  :label="t.phone"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="findName"
                  :label="t.customerName"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              <v-col cols="4">
                <v-btn
                  color="indigo"
                  dark
                  :loading="findingCustomerId !== null"
                  @click="doFindCustomer()"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  {{ t.search }}
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- Results -->
          <div
            v-for="(result, idx) in customerMatchResults"
            :key="idx"
            class="mb-4"
          >
            <div
              v-if="findingCustomerBulk"
              class="text-subtitle-2 font-weight-bold mb-2 pa-2 bg-light-grey"
            >
              <v-chip x-small label color="grey" dark class="mr-2">{{ result.transaction_ref }}</v-chip>
              {{ fmtLAK(result.amount) }}
            </div>

            <div v-if="result.matches && result.matches.length > 0">
              <v-card
                v-for="match in result.matches"
                :key="match.user_id"
                outlined
                class="mb-2 pa-3"
                :style="match.amount_matches ? 'border-left: 3px solid #4caf50;' : ''"
              >
                <v-alert
                  v-if="match.match_basis === 'amount_only'"
                  type="error"
                  dense
                  text
                  class="mb-2"
                >
                  {{ t.amountOnlyWarning }}
                </v-alert>
                <v-row dense align="center">
                  <v-col cols="8">
                    <div class="mb-1">
                      <span class="text-body-1 font-weight-bold">{{ match.name }}</span>
                      <span class="grey--text ml-2">{{ match.phone }}</span>
                      <v-chip
                        v-if="match.amount_matches"
                        x-small
                        label
                        color="green"
                        dark
                        class="ml-2"
                      >
                        {{ t.amountMatch }}
                      </v-chip>
                      <v-chip x-small label outlined class="ml-1">{{ match.status }}</v-chip>
                    </div>
                    <div
                      v-if="(match.customer && match.customer.village) || (match.customer && match.customer.district)"
                      class="text-body-2 mt-1"
                    >
                      <v-icon x-small color="grey" class="mr-1">mdi-map-marker</v-icon>
                      <span v-if="match.customer.village">{{ match.customer.village.name }}</span>
                      <span v-if="match.customer.village && match.customer.district">, </span>
                      <span v-if="match.customer.district">{{ match.customer.district.name }}</span>
                    </div>
                    <div class="text-body-2 mt-1">
                      {{ t.unpaidBillings }}: {{ match.unpaid_count || 0 }} — {{ fmtLAK(match.unpaid_total) }}
                    </div>
                    <!-- Expandable billings -->
                    <div
                      v-if="match.unpaid_billings && match.unpaid_billings.length > 0"
                      class="mt-1"
                    >
                      <div class="text-caption mb-1">{{ t.chooseBills }}</div>
                      <v-checkbox
                        v-for="bill in match.unpaid_billings"
                        :key="bill.id"
                        :input-value="isBillChosen(match, bill)"
                        dense
                        hide-details
                        class="mt-0 pl-2 text-caption"
                        @change="toggleBill(match, bill)"
                      >
                        <template v-slot:label>
                          <span class="text-caption">
                            #{{ bill.id }} — {{ bill.content }} — {{ fmtLAK(bill.total) }}
                            <v-chip v-if="bill.month_in_statement" x-small label color="green" dark class="ml-1">
                              {{ t.monthInStatement }}
                            </v-chip>
                          </span>
                        </template>
                      </v-checkbox>
                    </div>
                  </v-col>
                  <v-col cols="4" class="text-right">
                    <v-btn
                      small
                      color="green"
                      dark
                      :loading="linkingCustomerId === match.user_id"
                      @click="linkStatementCustomer(result.statement_record_id || findCustomerItem.id, match.user_id)"
                    >
                      <v-icon small left>mdi-link</v-icon>
                      {{ t.linkCustomer }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </div>
            <div v-else-if="!findingCustomerId" class="text-center pa-3 grey--text">
              <v-icon color="grey">mdi-account-off</v-icon>
              {{ t.noCustomerMatch }}
            </div>
          </div>

          <div v-if="customerMatchResults.length === 0" class="text-center pa-4 grey--text">
            <v-progress-circular v-if="findingCustomerBulk || findingCustomerId" indeterminate color="indigo" class="mr-2" />
            {{ (findingCustomerBulk || findingCustomerId) ? t.searching : t.searchToStart }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- QR Results (shown when no statements uploaded) -->
    <div v-show="!hasStatements">
      <!-- QR Summary Cards -->
      <v-row class="mb-4">
        <v-col
          v-for="card in summaryCards"
          :key="card.key"
          cols="12"
          sm="6"
          md
        >
          <v-card
            :color="card.color"
            dark
            class="pa-4 cursor-pointer summary-card"
            :class="{ 'elevation-6': activeTab === card.key, 'elevation-1': activeTab !== card.key }"
            @click="filterByTab(card.key)"
          >
            <div class="text-h4 font-weight-bold">
              {{ card.count }}
            </div>
            <div class="text-subtitle-2 font-weight-medium">
              {{ card.label }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card>
        <!-- Date Range Filter -->
        <v-card-text class="pb-0">
          <v-row
            dense
            align="center"
          >
            <v-col
              cols="12"
              sm="2"
            >
              <v-menu
                v-model="filterDateFromMenu"
                :close-on-content-click="true"
                min-width="auto"
                offset-y
                transition="scale-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filterDateFrom"
                    :label="t.dateFrom"
                    outlined
                    dense
                    readonly
                    clearable
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="filterDateFrom" />
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="2"
            >
              <v-menu
                v-model="filterDateToMenu"
                :close-on-content-click="true"
                min-width="auto"
                offset-y
                transition="scale-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filterDateTo"
                    :label="t.dateTo"
                    outlined
                    dense
                    readonly
                    clearable
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="filterDateTo" />
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-text-field
                v-model="search"
                :label="t.search"
                prepend-inner-icon="mdi-magnify"
                clearable
                dense
                outlined
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
              class="text-right"
            >
              <v-btn
                small
                color="#49A3DA"
                dark
                @click="applyFilters()"
              >
                <v-icon
                  small
                  left
                >
                  mdi-filter
                </v-icon>
                {{ t.applyFilter }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Match Status Tabs -->
        <v-tabs
          v-model="activeTab"
          background-color="tab-color lighten-2"
          dark
          slider-color="indigo lighten-5"
        >
          <v-tab href="#all">
            {{ t.all }} ({{ batch.total_records || 0 }})
          </v-tab>
          <v-tab href="#missed_payment">
            {{ t.missedPayments }} ({{ batch.missed_payment_count || 0 }})
          </v-tab>
          <v-tab href="#matched">
            {{ t.matched }} ({{ batch.matched_count || 0 }})
          </v-tab>
          <v-tab href="#expired">
            {{ t.expired }} ({{ batch.expired_count || 0 }})
          </v-tab>
          <v-tab href="#amount_mismatch">
            {{ t.mismatch }} ({{ batch.amount_mismatch_count || 0 }})
          </v-tab>
          <v-tab href="#ldb_error">
            {{ t.errors }} ({{ batch.ldb_error_count || 0 }})
          </v-tab>
        </v-tabs>

        <!-- Resolution Tabs -->
        <v-btn-toggle
          v-model="resolutionFilter"
          mandatory
          dense
          class="ma-3"
        >
          <v-btn
            small
            value="pending"
          >
            <v-icon
              small
              left
              color="orange"
            >
              mdi-timer-sand
            </v-icon>
            {{ t.unresolved }}
          </v-btn>
          <v-btn
            small
            value="approved"
          >
            <v-icon
              small
              left
              color="green"
            >
              mdi-check-circle
            </v-icon>
            {{ t.approved }}
          </v-btn>
          <v-btn
            small
            value="rejected"
          >
            <v-icon
              small
              left
              color="red"
            >
              mdi-close-circle
            </v-icon>
            {{ t.rejected }}
          </v-btn>
          <v-btn
            small
            value="all"
          >
            <v-icon
              small
              left
            >
              mdi-format-list-bulleted
            </v-icon>
            {{ t.all }}
          </v-btn>
        </v-btn-toggle>

        <!-- Bulk Action Bar -->
        <v-toolbar
          v-if="selectedItems.length > 0"
          flat
          dense
          color="grey lighten-4"
        >
          <span class="mr-4 font-weight-bold">{{ selectedItems.length }} {{ t.selected }}</span>
          <v-btn
            small
            color="green"
            dark
            class="mr-2"
            @click="confirmBulkApprove()"
          >
            {{ t.approveSelected }}
          </v-btn>
          <v-btn
            small
            color="red"
            dark
            @click="showRejectModal = true"
          >
            {{ t.rejectSelected }}
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-0">
          <v-data-table
            v-model="selectedItems"
            :headers="headers"
            :items="filteredRecords"
            :search="search"
            :show-select="hasPendingItems"
            :items-per-page="perPage"
            :footer-props="footerProps"
            item-key="id"
            class="eod-table"
            :item-class="rowClass"
            @update:items-per-page="onPerPageChange"
          >
            <template v-slot:item.data-table-select="{ item, isSelected, select }">
              <v-simple-checkbox
                v-if="item.resolution_status === 'pending'"
                :value="isSelected"
                @input="select($event)"
              />
            </template>

            <template v-slot:item.qr_amount="{ item }">
              <span class="font-weight-bold">{{ formatCurrency(item.qr_amount) }}</span>
            </template>

            <template v-slot:item.qr_status="{ item }">
              <v-chip
                small
                label
                :color="qrStatusColor(item.qr_status)"
                dark
                class="font-weight-bold"
              >
                {{ item.qr_status }}
              </v-chip>
            </template>

            <template v-slot:item.ldb_status="{ item }">
              <v-chip
                small
                label
                :color="ldbStatusColor(item.ldb_status)"
                dark
                class="font-weight-bold"
              >
                {{ item.ldb_status || '-' }}
              </v-chip>
            </template>

            <template v-slot:item.ldb_amount="{ item }">
              <span :class="{ 'red--text font-weight-bold': item.ldb_amount && item.ldb_amount !== item.qr_amount }">
                {{ item.ldb_amount ? formatCurrency(item.ldb_amount) : '-' }}
              </span>
            </template>

            <template v-slot:item.customer="{ item }">
              {{ item.qr_generator && item.qr_generator.owner ? item.qr_generator.owner.name : '-' }}
            </template>

            <template v-slot:item.ldb_payment_at="{ item }">
              {{ item.ldb_payment_at ? moment(item.ldb_payment_at).format("DD-MM-YYYY HH:mm") : '-' }}
            </template>

            <template v-slot:item.match_status="{ item }">
              <v-chip
                small
                label
                :color="matchStatusColor(item.match_status)"
                dark
                class="font-weight-bold text-capitalize"
              >
                {{ formatMatchStatus(item.match_status) }}
              </v-chip>
            </template>

            <template v-slot:item.resolution_status="{ item }">
              <v-chip
                small
                label
                :color="resolutionColor(item.resolution_status)"
                dark
                class="font-weight-bold text-capitalize"
              >
                {{ formatResolution(item.resolution_status) }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <template v-if="item.resolution_status === 'pending'">
                <v-btn
                  x-small
                  color="green"
                  dark
                  class="mr-1 font-weight-bold"
                  @click="confirmSingleApprove(item)"
                >
                  {{ t.approve }}
                </v-btn>
                <v-btn
                  x-small
                  color="red"
                  dark
                  class="font-weight-bold"
                  @click="startSingleReject(item)"
                >
                  {{ t.reject }}
                </v-btn>
              </template>
              <v-icon
                v-else-if="item.resolution_status === 'approved'"
                color="green"
                small
              >
                mdi-check-circle
              </v-icon>
              <span v-else>-</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>

    <!-- Approve Confirmation Dialog -->
    <v-dialog
      v-model="showApproveDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>{{ t.confirmApproval }}</v-card-title>
        <v-card-text>
          <p class="mb-4">
            {{ t.confirmApproveMsg.replace('{n}', approveIds.length) }}
          </p>
          <v-menu
            v-model="approveDateMenu"
            :close-on-content-click="true"
            :nudge-right="40"
            min-width="auto"
            offset-y
            transition="scale-transition"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="approveDate"
                :label="t.transactionDate"
                outlined
                dense
                readonly
                prepend-inner-icon="mdi-calendar"
                :hint="t.transactionDateHint"
                persistent-hint
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker v-model="approveDate" />
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showApproveDialog = false"
          >
            {{ t.cancel }}
          </v-btn>
          <v-btn
            color="green"
            dark
            :loading="actionLoading"
            :disabled="!approveDate"
            @click="doApprove()"
          >
            {{ t.approve }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Approve Statement Dialog (no date needed) -->
    <v-dialog
      v-model="showApproveStmtDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>{{ t.confirmApproval }}</v-card-title>
        <v-card-text>
          <p>{{ t.confirmApproveMsg.replace('{n}', approveStmtIds.length) }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showApproveStmtDialog = false"
          >
            {{ t.cancel }}
          </v-btn>
          <v-btn
            color="green"
            dark
            :loading="actionLoading"
            @click="doApproveStatement()"
          >
            {{ t.approve }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Modal -->
    <v-dialog
      v-model="showRejectModal"
      max-width="500"
    >
      <v-card>
        <v-card-title>{{ t.rejectItems }}</v-card-title>
        <v-card-text>
          <p>{{ t.rejectMsg.replace('{n}', rejectIds.length) }}</p>
          <v-textarea
            v-model="rejectReason"
            outlined
            dense
            :label="t.rejectionReason"
            rows="3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeRejectModal()"
          >
            {{ t.cancel }}
          </v-btn>
          <v-btn
            color="red"
            dark
            :loading="actionLoading"
            :disabled="!rejectReason.trim()"
            @click="doReject()"
          >
            {{ t.reject }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Statement Modal -->
    <v-dialog
      v-model="showRejectStmtModal"
      max-width="500"
    >
      <v-card>
        <v-card-title>{{ t.rejectItems }}</v-card-title>
        <v-card-text>
          <p>{{ t.rejectMsg.replace('{n}', rejectStmtIds.length) }}</p>
          <v-textarea
            v-model="rejectStmtReason"
            outlined
            dense
            :label="t.rejectionReason"
            rows="3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeRejectStmtModal()"
          >
            {{ t.cancel }}
          </v-btn>
          <v-btn
            color="red"
            dark
            :loading="actionLoading"
            :disabled="!rejectStmtReason.trim()"
            @click="doRejectStatement()"
          >
            {{ t.reject }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Evidence Dialog -->
    <evidence-dialog
      v-model="showEvidenceDialog"
      :record="evidenceRecord"
      @confirmed="onEvidenceConfirmed"
    />
  </v-container>
</template>

<script>
import moment from 'moment';
import SubReconciliationCard from './components/SubReconciliationCard.vue';
import EodSummaryPanel from './components/EodSummaryPanel.vue';
import EvidenceDialog from './components/EvidenceDialog.vue';

export default {
  name: 'LdbReconciliationDetail',
  components: { SubReconciliationCard, EodSummaryPanel, EvidenceDialog },
  title() {
    return 'Vientiane Waste Co-Dev | EOD Reconciliation Detail';
  },
  data() {
    return {
      lang: 'lo',
      mainView: 'qr',
      translations: {
        lo: {
          backToBatchList: 'ກັບຄືນລາຍການ',
          export: 'ສົ່ງອອກ',
          all: 'ທັງໝົດ',
          missedPayments: 'ຂາດການຊຳລະ',
          matched: 'ກົງກັນ',
          expired: 'ໝົດອາຍຸ',
          mismatch: 'ຈຳນວນບໍ່ກົງ',
          errors: 'ຜິດພາດ',
          selected: 'ລາຍການທີ່ເລືອກ',
          approveSelected: 'ອະນຸມັດທີ່ເລືອກ',
          rejectSelected: 'ປະຕິເສດທີ່ເລືອກ',
          approve: 'ອະນຸມັດ',
          reject: 'ປະຕິເສດ',
          cancel: 'ຍົກເລີກ',
          confirmApproval: 'ຢືນຢັນການອະນຸມັດ',
          confirmApproveMsg: 'ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການອະນຸມັດ {n} ລາຍການ?',
          rejectItems: 'ປະຕິເສດລາຍການ',
          rejectMsg: 'ກຳລັງປະຕິເສດ {n} ລາຍການ. ກະລຸນາລະບຸເຫດຜົນ:',
          rejectionReason: 'ເຫດຜົນການປະຕິເສດ',
          billNumber: 'ເລກບິນ',
          customer: 'ລູກຄ້າ',
          qrAmount: 'ຈຳນວນ QR',
          systemStatus: 'ສະຖານະລະບົບ',
          ldbStatus: 'ສະຖານະ LDB',
          ldbAmount: 'ຈຳນວນ LDB',
          ldbRef: 'ເລກອ້າງອີງ LDB',
          ldbPayer: 'ຜູ້ຊຳລະ LDB',
          paymentDate: 'ວັນທີຊຳລະ',
          matchStatus: 'ສະຖານະການກວດ',
          resolution: 'ຜົນການພິຈາລະນາ',
          actions: 'ຈັດການ',
          search: 'ຄົ້ນຫາ',
          dateFrom: 'ວັນທີເລີ່ມ',
          dateTo: 'ວັນທີສິ້ນສຸດ',
          applyFilter: 'ກັ່ນຕອງ',
          unresolved: 'ຍັງບໍ່ແກ້ໄຂ',
          approved: 'ອະນຸມັດແລ້ວ',
          rejected: 'ປະຕິເສດແລ້ວ',
          transactionDate: 'ວັນທີທຸລະກຳທະນາຄານ',
          transactionDateHint: 'ເລືອກວັນທີຕາມໃບແຈ້ງຍອດທະນາຄານ (ສາມາດຍ້ອນຫຼັງໄດ້)',
          uploadStatement: 'ອັບໂຫຼດໃບແຈ້ງທະນາຄານ',
          qrResults: 'ຜົນ QR',
          bcelStatement: 'ໃບແຈ້ງ BCEL',
          ldbStatement: 'ໃບແຈ້ງ LDB',
          notUploaded: 'ຍັງບໍ່ໄດ້ອັບໂຫຼດ',
          category: 'ລາຍການ',
          qty: 'ຈຳນວນ',
          amountLak: 'ມູນຄ່າ (LAK)',
          totalStatement: 'ລວມໃບແຈ້ງ',
          reconciliationSettlement: 'ສະຫຼຸບການກວດສອບ',
          settlementItem: 'ລາຍການ',
          bankStatementTotal: 'ລວມຈາກໃບແຈ້ງທະນາຄານ',
          systemConfirmed: 'ລະບົບຢືນຢັນ',
          difference: 'ຜົນຕ່າງ',
          pendingReview: 'ລໍຖ້າການກວດ',
          resolveToClose: 'ແກ້ໄຂເພື່ອປິດ EOD',
          total: 'ລວມ',
          searchQr: 'ຄົ້ນຫາ QR',
          qrMatchResults: 'ຜົນການຄົ້ນຫາ QR',
          linkQr: 'ເຊື່ອມ',
          noQrMatch: 'ບໍ່ພົບ QR ທີ່ກົງກັນ',
          searching: 'ກຳລັງຄົ້ນຫາ...',
          billings: 'ບິນ',
          findCustomer: 'ຄົ້ນຫາລູກຄ້າ',
          findCustomerResults: 'ຜົນການຄົ້ນຫາລູກຄ້າ',
          linkCustomer: 'ເຊື່ອມລູກຄ້າ',
          noCustomerMatch: 'ບໍ່ພົບລູກຄ້າ',
          amountOnlyWarning: 'ພົບຈາກຍອດເງິນຢ່າງດຽວ: ມີລູກຄ້າຫຼາຍຄົນທີ່ຄ້າງຍອດນີ້. ກວດຜູ້ຈ່າຍໃຫ້ແນ່ໃຈກ່ອນເຊື່ອມ.',
          chooseBills: 'ເລືອກບິນທີ່ຈະຕັດ:',
          monthInStatement: 'ເດືອນຕາມໃບແຈ້ງ',
          basisCustomerId: 'ລະຫັດລູກຄ້າ',
          basisPhone: 'ເບີໂທ',
          basisStaff: 'ພະນັກງານເລືອກ',
          amountMatch: 'ຈຳນວນກົງ',
          unpaidBillings: 'ບິນຄ້າງຊຳລະ',
          customerName: 'ຊື່ລູກຄ້າ',
          searchToStart: 'ກົດຄົ້ນຫາເພື່ອເລີ່ມ',
          open: 'ເປີດ',
          closed: 'ປິດແລ້ວ',
          actualReceived: 'ຈຳນວນທີ່ໄດ້ຮັບຕົວຈິງ',
          comment: 'ໝາຍເຫດ',
          save: 'ບັນທຶກ',
          closeEod: 'ປິດ EOD',
          closedBy: 'ປິດໂດຍ',
          confirmCloseTitle: 'ຢືນຢັນການປິດ EOD',
          confirmCloseMsg: 'ຜົນຕ່າງເປັນ 0. ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການປິດ?',
          proposeClose: 'ສະເໜີປິດ',
          proposeCloseMsg: 'ມີຜົນຕ່າງ ທ່ານຕ້ອງສະເໜີປິດ EOD ເພື່ອຂໍອະນຸມັດຈາກ ຜູ້ຈັດການ IT ແລະ ຜູ້ຈັດການການເງິນ.',
          proposeReason: 'ເຫດຜົນການສະເໜີ',
          proposeReasonHint: 'ອະທິບາຍສາເຫດຂອງຜົນຕ່າງ',
          pendingApproval: 'ລໍຖ້າການອະນຸມັດ',
          proposedBy: 'ສະເໜີໂດຍ',
          itManager: 'ຜູ້ຈັດການ IT',
          financeManager: 'ຜູ້ຈັດການການເງິນ',
          approvedStatus: 'ອະນຸມັດແລ້ວ',
          rejectedStatus: 'ປະຕິເສດແລ້ວ',
          pendingStatus: 'ລໍຖ້າ',
          closeRejected: 'ການສະເໜີປິດຖືກປະຕິເສດ',
          threePeopleRule: 'ຜູ້ສະເໜີ, ຜູ້ອະນຸມັດ IT ແລະ ຜູ້ອະນຸມັດການເງິນ ຕ້ອງເປັນ 3 ຄົນຕ່າງກັນ.',
          unallocated: 'ເງິນທີ່ບໍ່ໄດ້ຕັດບິນ',
          rejectClose: 'ປະຕິເສດການປິດ',
          uploaded: 'ອັບໂຫຼດແລ້ວ',
          rowsInFile: 'ແຖວໃນໄຟລ໌',
          rowsInRange: 'ແຖວໃນຊ່ວງວັນທີ',
          noRowsMatchDate: 'ບໍ່ມີແຖວກົງກັບຊ່ວງວັນທີ batch',
          rowsParsed: 'ແຖວທີ່ວິເຄາະ',
          notInSystem: 'ບໍ່ມີໃນລະບົບ',
          paidNotInStatement: 'ຈ່າຍແລ້ວ ບໍ່ມີໃນໃບແຈ້ງ',
          stmtDate: 'ວັນທີໃບແຈ້ງ',
          systemDate: 'ວັນທີລະບົບ',
          reference: 'ເລກອ້າງອີງ',
          phone: 'ເບີໂທ',
          stmtAmount: 'ຈຳນວນໃບແຈ້ງ',
          systemAmount: 'ຈຳນວນລະບົບ',
          paymentType: 'ປະເພດ',
          notes: 'ໝາຍເຫດ',
          qrConfirmed: 'QR ຢືນຢັນ',
          bcelMatched: 'BCEL ກົງກັນ',
          ldbMatched: 'LDB ກົງກັນ',
          totalSystem: 'ລວມລະບົບ',
          bankAmount: 'ຈຳນວນທະນາຄານ',
          qrBill: 'ເລກບິນ QR',
          qrCustomer: 'ລູກຄ້າ (QR)',
          qrBillingIds: 'ບິນ IDs',
          qrStatus: 'ສະຖານະ QR',
          ldbStatus2: 'ສະຖານະ LDB',
          crossBankQr: 'QR ຂ້າມທະນາຄານ',
          ldbTrustQr: 'LDB Trust QR',
          bcelBillPay: 'BCEL ຊຳລະບິນ',
          ldbBillPay: 'LDB ຊຳລະບິນ',
          qrQuickCheck: 'ກວດ QR ດ່ວນ',
          maxFileSize: 'ຂະໜາດສູງສຸດ 10MB',
          notApplicable: 'ບໍ່ກ່ຽວຂ້ອງ',
          pending: 'ລໍຖ້າ',
          missedPayment: 'ຂາດການຊຳລະ',
          transactionTime: 'ເວລາທຸລະກຳ',
          billingId: 'ລະຫັດບິນ',
        },
        en: {
          backToBatchList: 'Back to Batch List',
          export: 'Export',
          all: 'All',
          missedPayments: 'Missed Payments',
          matched: 'Matched',
          expired: 'Expired',
          mismatch: 'Mismatch',
          errors: 'Errors',
          selected: 'selected',
          approveSelected: 'Approve Selected',
          rejectSelected: 'Reject Selected',
          approve: 'Approve',
          reject: 'Reject',
          cancel: 'Cancel',
          confirmApproval: 'Confirm Approval',
          confirmApproveMsg: 'Are you sure you want to approve {n} item(s)?',
          rejectItems: 'Reject Items',
          rejectMsg: 'Rejecting {n} item(s). Please provide a reason:',
          rejectionReason: 'Rejection reason',
          billNumber: 'Bill Number',
          customer: 'Customer',
          qrAmount: 'QR Amount',
          systemStatus: 'System Status',
          ldbStatus: 'LDB Status',
          ldbAmount: 'LDB Amount',
          ldbRef: 'LDB Ref',
          ldbPayer: 'LDB Payer',
          paymentDate: 'Payment Date',
          matchStatus: 'Match Status',
          resolution: 'Resolution',
          actions: 'Actions',
          search: 'Search',
          dateFrom: 'Date From',
          dateTo: 'Date To',
          applyFilter: 'Apply Filter',
          unresolved: 'Unresolved',
          approved: 'Approved',
          rejected: 'Rejected',
          transactionDate: 'Bank Transaction Date',
          transactionDateHint: 'Select the date from bank statement (can be backdated)',
          uploadStatement: 'Upload Bank Statement',
          qrResults: 'QR Results',
          bcelStatement: 'BCEL Statement',
          ldbStatement: 'LDB Statement',
          notUploaded: 'Not uploaded',
          category: 'Category',
          qty: 'Qty',
          amountLak: 'Amount (LAK)',
          totalStatement: 'Total Statement',
          reconciliationSettlement: 'Reconciliation Settlement',
          settlementItem: 'Item',
          bankStatementTotal: 'Bank Statement Total',
          systemConfirmed: 'System Confirmed',
          difference: 'Difference',
          pendingReview: 'Pending Review',
          resolveToClose: 'Resolve to close EOD',
          total: 'Total',
          searchQr: 'Search QR',
          qrMatchResults: 'QR Match Results',
          linkQr: 'Link',
          noQrMatch: 'No matching QR found',
          searching: 'Searching...',
          billings: 'Billings',
          findCustomer: 'Find Customer',
          findCustomerResults: 'Customer Search Results',
          linkCustomer: 'Link Customer',
          noCustomerMatch: 'No customer found',
          amountOnlyWarning: 'Found by amount only: many customers owe this amount. Check the payer before linking.',
          chooseBills: 'Bills to pay:',
          monthInStatement: 'Month in statement',
          basisCustomerId: 'Customer ID',
          basisPhone: 'Phone',
          basisStaff: 'Staff choice',
          amountMatch: 'Amount Match',
          unpaidBillings: 'Unpaid Billings',
          customerName: 'Customer Name',
          searchToStart: 'Click search to start',
          open: 'Open',
          closed: 'Closed',
          actualReceived: 'Actual Received',
          comment: 'Comment',
          save: 'Save',
          closeEod: 'Close EOD',
          closedBy: 'Closed by',
          confirmCloseTitle: 'Confirm Close EOD',
          confirmCloseMsg: 'Difference is 0. Are you sure you want to close?',
          proposeClose: 'Propose Close',
          proposeCloseMsg: 'There is a difference. You need to propose closing this EOD for approval by IT Manager and Finance Manager.',
          proposeReason: 'Reason for proposal',
          proposeReasonHint: 'Explain the cause of the difference',
          pendingApproval: 'Pending Approval',
          proposedBy: 'Proposed by',
          itManager: 'IT Manager',
          financeManager: 'Finance Manager',
          approvedStatus: 'Approved',
          rejectedStatus: 'Rejected',
          pendingStatus: 'Pending',
          closeRejected: 'Close proposal rejected',
          threePeopleRule: 'The proposer, the IT approver and the Finance approver must be three different people.',
          unallocated: 'Not allocated to a bill',
          rejectClose: 'Reject Close',
          uploaded: 'Uploaded',
          rowsInFile: 'rows in file',
          rowsInRange: 'rows in date range',
          noRowsMatchDate: 'No rows match the batch date range',
          rowsParsed: 'rows parsed',
          notInSystem: 'Not in System',
          paidNotInStatement: 'Paid, Not in Statement',
          stmtDate: 'Stmt Date',
          systemDate: 'System Date',
          reference: 'Reference',
          phone: 'Phone',
          stmtAmount: 'Statement Amount',
          systemAmount: 'System Amount',
          paymentType: 'Type',
          notes: 'Notes',
          qrConfirmed: 'QR Confirmed',
          bcelMatched: 'BCEL Matched',
          ldbMatched: 'LDB Matched',
          totalSystem: 'Total System',
          bankAmount: 'Bank Amount',
          qrBill: 'QR Bill',
          qrCustomer: 'Customer (QR)',
          qrBillingIds: 'Billing IDs',
          qrStatus: 'QR Status',
          ldbStatus2: 'LDB Status',
          crossBankQr: 'Cross-bank QR',
          ldbTrustQr: 'LDB Trust QR',
          bcelBillPay: 'BCEL Bill Pay',
          ldbBillPay: 'LDB Bill Pay',
          qrQuickCheck: 'QR Quick Check',
          maxFileSize: 'Max file size 10MB',
          notApplicable: 'Not Applicable',
          pending: 'Pending',
          missedPayment: 'Missed Payment',
          transactionTime: 'Transaction Time',
          billingId: 'Billing ID',
        },
      },
      // Statement data
      bankFilter: 'BCEL',
      // Default to 'all' so the bank-vs-system comparison table is populated on
      // load. Defaulting to 'missed_payment' left the table blank for any batch
      // with zero missed payments (the healthy case) — looked like no data.
      stmtMatchFilter: 'all',
      stmtResolutionFilter: 'all',
      stmtSearch: '',
      stmtRecords: [],
      stmtSummary: { BCEL: null, LDB: null },
      selectedStmtItems: [],
      enquiringId: null,
      enquiringBulk: false,
      enquiringItem: {},
      showQrMatchModal: false,
      qrMatchResults: [],
      linkingId: null,
      findingCustomerId: null,
      findingCustomerBulk: false,
      findCustomerItem: null,
      showFindCustomerModal: false,
      customerMatchResults: [],
      linkingCustomerId: null,
      // user_id → bill ids staff ticked in the find-customer dialog (sent with link-statement-customer)
      linkBillSelection: {},
      actualReceived: '',
      settlementComment: '',
      savingSettlement: false,
      closingBatch: false,
      showCloseEodDialog: false,
      showProposeDialog: false,
      proposingClose: false,
      proposeComment: '',
      approvingIt: false,
      approvingFinance: false,
      showRejectCloseDialog: false,
      rejectRole: '',
      rejectCloseReason: '',
      findPhone: '',
      findName: '',
      bcelFile: null,
      ldbFile: null,
      uploadingBcel: false,
      uploadingLdb: false,
      // Statement approve/reject
      showApproveStmtDialog: false,
      approveStmtIds: [],
      showRejectStmtModal: false,
      rejectStmtIds: [],
      rejectStmtReason: '',
      // QR fallback data
      batch: {},
      subReconciliations: [],
      availableAccounts: [],
      eodSummary: null,
      generatingSummary: false,
      activeSubReconciliationId: null,
      showEvidenceDialog: false,
      evidenceRecord: {},
      bulkEvidenceQueue: [],
      bulkApproveIds: [],
      records: [],
      selectedItems: [],
      pagination: {},
      // Default to 'all' so the records tab isn't blank when a batch has no
      // missed payments (same reason as stmtMatchFilter above).
      activeTab: 'all',
      resolutionFilter: 'pending',
      filterDateFrom: '',
      filterDateTo: '',
      filterDateFromMenu: false,
      filterDateToMenu: false,
      search: '',
      // Common
      showApproveDialog: false,
      showRejectModal: false,
      approveIds: [],
      approveDate: '',
      approveDateMenu: false,
      rejectIds: [],
      rejectReason: '',
      actionLoading: false,
      perPage: 25,
      footerProps: {
        'items-per-page-options': [25, 50, 100, 500, 1000],
      },
      headers: [],
      stmtHeaders: [],
    };
  },
  computed: {
    t() {
      return this.translations[this.lang];
    },
    // Selected statement rows a QR search can explain. An IN APP row (LDB Trust in-app bill
    // payment) is not a LaoQR payment, so searching QR codes for it finds nothing useful.
    qrSearchableStmtItems() {
      return this.selectedStmtItems.filter((i) => i.match_status === 'not_in_system'
        && !(i.payment_type === 'BILLPAY' && i.bank !== 'BCEL'));
    },
    currentUserId() {
      const user = this.$store.getters['auth/auth'];
      return user && user.id ? Number(user.id) : null;
    },
    batchId() {
      return this.$route.params.batchId;
    },
    hasStatements() {
      const bcel = this.stmtSummary.BCEL;
      const ldb = this.stmtSummary.LDB;
      return !!((bcel && bcel.uploaded) || (ldb && ldb.uploaded));
    },
    activeSummary() {
      return this.stmtSummary[this.bankFilter] || {};
    },
    totalBankAmount() {
      const bcel = this.stmtSummary.BCEL ? Number(this.stmtSummary.BCEL.total_amount || 0) : 0;
      const ldb = this.stmtSummary.LDB ? Number(this.stmtSummary.LDB.total_amount || 0) : 0;
      return bcel + ldb;
    },
    totalSystemConfirmed() {
      const bcelM = this.stmtSummary.BCEL ? Number(this.stmtSummary.BCEL.matched_amount || 0) : 0;
      const ldbM = this.stmtSummary.LDB ? Number(this.stmtSummary.LDB.matched_amount || 0) : 0;
      const bcelA = this.stmtSummary.BCEL ? Number(this.stmtSummary.BCEL.approved_amount || 0) : 0;
      const ldbA = this.stmtSummary.LDB ? Number(this.stmtSummary.LDB.approved_amount || 0) : 0;
      return bcelM + ldbM + bcelA + ldbA;
    },
    settlementDiff() {
      if (this.totalBankAmount === 0) return '-';
      return this.totalBankAmount - this.totalSystemConfirmed;
    },
    totalPending() {
      const bcel = this.stmtSummary.BCEL ? Number(this.stmtSummary.BCEL.pending_count || 0) : 0;
      const ldb = this.stmtSummary.LDB ? Number(this.stmtSummary.LDB.pending_count || 0) : 0;
      return bcel + ldb;
    },
    combinedSummary() {
      const bcel = this.stmtSummary.BCEL || {};
      const ldb = this.stmtSummary.LDB || {};
      const add = (a, b) => Number(a || 0) + Number(b || 0);
      return {
        matched_count: add(bcel.matched_count, ldb.matched_count),
        matched_amount: add(bcel.matched_amount, ldb.matched_amount),
        missed_payment_count: add(bcel.missed_payment_count, ldb.missed_payment_count),
        missed_payment_amount: add(bcel.missed_payment_amount, ldb.missed_payment_amount),
        not_in_system_count: add(bcel.not_in_system_count, ldb.not_in_system_count),
        not_in_system_amount: add(bcel.not_in_system_amount, ldb.not_in_system_amount),
        amount_mismatch_count: add(bcel.amount_mismatch_count, ldb.amount_mismatch_count),
        amount_mismatch_amount: add(bcel.amount_mismatch_amount, ldb.amount_mismatch_amount),
        expired_count: add(bcel.expired_count, ldb.expired_count),
        expired_amount: add(bcel.expired_amount, ldb.expired_amount),
        paid_not_in_statement_count: add(bcel.paid_not_in_statement_count, ldb.paid_not_in_statement_count),
        paid_not_in_statement_amount: add(bcel.paid_not_in_statement_amount, ldb.paid_not_in_statement_amount),
      };
    },
    filteredRecords() {
      return this.records;
    },
    hasPendingItems() {
      return this.records.some((r) => r.resolution_status === 'pending');
    },
    hasPendingStmtItems() {
      return this.stmtRecords.some((r) => r.resolution_status === 'pending');
    },
    summaryCards() {
      return [
        {
          key: 'matched', label: this.t.matched, count: this.batch.matched_count || 0, color: 'green',
        },
        {
          key: 'missed_payment', label: this.t.missedPayments, count: this.batch.missed_payment_count || 0, color: 'red',
        },
        {
          key: 'expired', label: this.t.expired, count: this.batch.expired_count || 0, color: 'grey',
        },
        {
          key: 'amount_mismatch', label: this.t.mismatch, count: this.batch.amount_mismatch_count || 0, color: 'orange',
        },
        {
          key: 'ldb_error', label: this.t.errors, count: this.batch.ldb_error_count || 0, color: 'amber darken-3',
        },
      ];
    },
    stmtSummaryCards() {
      const s = this.combinedSummary;
      return [
        {
          key: 'matched', label: this.t.matched, count: s.matched_count || 0, amount: s.matched_amount || 0, color: 'green',
        },
        {
          key: 'missed_payment', label: this.t.missedPayments, count: s.missed_payment_count || 0, amount: s.missed_payment_amount || 0, color: 'red',
        },
        {
          key: 'not_in_system', label: this.t.notInSystem, count: s.not_in_system_count || 0, amount: s.not_in_system_amount || 0, color: 'orange',
        },
        {
          key: 'amount_mismatch', label: this.t.mismatch, count: s.amount_mismatch_count || 0, amount: s.amount_mismatch_amount || 0, color: 'amber darken-3',
        },
        {
          key: 'expired', label: this.t.expired, count: s.expired_count || 0, amount: s.expired_amount || 0, color: 'grey',
        },
        {
          key: 'paid_not_in_statement', label: this.t.paidNotInStatement, count: s.paid_not_in_statement_count || 0, amount: s.paid_not_in_statement_amount || 0, color: 'blue',
        },
      ];
    },
    stmtMatchFilterOptions() {
      return [
        { text: this.t.all, value: 'all' },
        { text: this.t.matched, value: 'matched' },
        { text: this.t.missedPayments, value: 'missed_payment' },
        { text: this.t.notInSystem, value: 'not_in_system' },
        { text: this.t.mismatch, value: 'amount_mismatch' },
        { text: this.t.expired, value: 'expired' },
        { text: this.t.paidNotInStatement, value: 'paid_not_in_statement' },
      ];
    },
    stmtResolutionFilterOptions() {
      return [
        { text: this.t.all, value: 'all' },
        { text: this.t.pending, value: 'pending' },
        { text: this.t.approved, value: 'approved' },
        { text: this.t.rejected, value: 'rejected' },
        { text: this.t.notApplicable, value: 'not_applicable' },
      ];
    },
    allSubsComplete() {
      return this.subReconciliations.length > 0
        && this.subReconciliations.every((s) => s.status === 'completed');
    },
  },
  watch: {
    activeTab() {
      this.selectedItems = [];
      this.fetchRecords();
    },
    resolutionFilter() {
      this.selectedItems = [];
      this.fetchRecords();
    },
    bankFilter() {
      this.selectedStmtItems = [];
      this.fetchStatementRecords();
    },
    stmtMatchFilter() {
      this.selectedStmtItems = [];
      this.fetchStatementRecords();
    },
    stmtResolutionFilter() {
      this.selectedStmtItems = [];
      this.fetchStatementRecords();
    },
    lang() {
      this.updateHeaders();
      this.updateStmtHeaders();
    },
  },
  created() {
    this.updateHeaders();
    this.updateStmtHeaders();
    if (this.$route.query.match_status) {
      this.stmtMatchFilter = this.$route.query.match_status;
    }
    if (this.$route.query.resolution_status) {
      this.stmtResolutionFilter = this.$route.query.resolution_status;
    }
    this.fetchBatch();
    this.fetchRecords();
    this.fetchSubReconciliations();
  },
  methods: {
    moment,
    fmtLAK(amount) {
      if (amount === null || amount === undefined || amount === '-') return '-';
      return `\u20AD${Intl.NumberFormat().format(Math.round(Number(amount)))}`;
    },
    // Sub-reconciliation methods
    async fetchSubReconciliations() {
      try {
        const res = await this.$axios.get(`ldb-reconciliation/${this.batchId}/sub-reconciliations`);
        if (res.data.code == 200 || res.data.success) {
          this.subReconciliations = res.data.data.sub_reconciliations || [];
          this.availableAccounts = res.data.data.available_accounts || [];
        }
      } catch (e) {
        console.error('Failed to fetch sub-reconciliations', e);
      }
    },
    async createSubReconciliation(bankAccountId) {
      try {
        await this.$axios.post(`ldb-reconciliation/${this.batchId}/sub-reconciliations`, { bank_account_id: bankAccountId });
        this.fetchSubReconciliations();
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      }
    },
    onSubUploaded() {
      this.fetchSubReconciliations();
      this.fetchBatch();
    },
    async completeSubReconciliation(sub) {
      try {
        const res = await this.$axios.post(`ldb-reconciliation/${this.batchId}/sub-reconciliations/${sub.id}/complete`);
        if (res.data.data.warning) {
          if (window.confirm(`ມີ ${res.data.data.pending_count} ລາຍການທີ່ຍັງບໍ່ກວດສອບ. ສຳເລັດແນວໃດ?`)) {
            await this.$axios.post(`ldb-reconciliation/${this.batchId}/sub-reconciliations/${sub.id}/complete`, { force: true });
          } else {
            return;
          }
        }
        this.fetchSubReconciliations();
        this.$store.commit('Toast_State', { value: true, color: 'success', msg: 'ສຳເລັດແລ້ວ' });
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      }
    },
    async reopenSubReconciliation(sub) {
      try {
        await this.$axios.post(`ldb-reconciliation/${this.batchId}/sub-reconciliations/${sub.id}/reopen`);
        this.fetchSubReconciliations();
        this.eodSummary = null;
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      }
    },
    viewSubDetail(sub) {
      // Switch to statement view filtered by this sub-reconciliation
      this.mainView = 'statement';
      this.bankFilter = sub.bank_account.bank;
      this.activeSubReconciliationId = sub.id;
      this.fetchStatementRecords();
    },
    async generateEodSummary() {
      this.generatingSummary = true;
      try {
        const res = await this.$axios.post(`ldb-reconciliation/${this.batchId}/eod-summary`);
        if (res.data.code == 200 || res.data.success) {
          this.eodSummary = res.data.data;
          this.fetchBatch();
        }
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.generatingSummary = false;
      }
    },
    async closeEod() {
      try {
        await this.$axios.put(`ldb-reconciliation/${this.batchId}`, this.closePayload());
        this.$store.commit('Toast_State', { value: true, color: 'success', msg: 'EOD ປິດສຳເລັດ' });
        this.fetchBatch();
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      }
    },
    fetchBatchData() {
      this.fetchBatch();
      this.fetchSubReconciliations();
    },
    onEvidenceConfirmed(record) {
      this.fetchStatementRecords();
      // If bulk evidence queue has more items, process next
      if (this.bulkEvidenceQueue.length > 0) {
        this.processNextEvidence();
        return;
      }
      // If bulk approve flow, show approval for all selected
      if (this.bulkApproveIds.length > 0) {
        this.approveStmtIds = this.bulkApproveIds;
        this.bulkApproveIds = [];
        this.showApproveStmtDialog = true;
        return;
      }
      // Single approve flow
      this.approveStmtIds = [record.id];
      this.showApproveStmtDialog = true;
    },
    bankConfirmed(bank) {
      const s = this.stmtSummary[bank];
      if (!s) return 0;
      return Number(s.matched_amount || 0) + Number(s.approved_amount || 0);
    },
    bankDiff(bank) {
      const s = this.stmtSummary[bank];
      if (!s) return '-';
      const bankTotal = Number(s.total_amount || 0);
      const confirmed = Number(s.matched_amount || 0) + Number(s.approved_amount || 0);
      return bankTotal - confirmed;
    },
    paymentTypeColor(type) {
      if (!type) return 'grey';
      switch (type) {
        case 'BPAY': return 'blue';
        case 'BILLPAY': return 'purple';
        case 'QR_LAOQR_CROSSBANK': return 'teal';
        case 'QR_LDB_TRUST': return 'indigo';
        default: return 'grey';
      }
    },
    // LDB rows: IN APP (LDB Trust bill payment, booked LDB_IN_APP) or MERCHANT (LaoQR, LDB_MERCHANT)
    paymentTypeLabel(type, bank) {
      if (!type) return '-';
      switch (type) {
        case 'BPAY': return this.t.bcelBillPay;
        case 'BILLPAY': return bank === 'BCEL' ? this.t.ldbBillPay : 'IN APP';
        case 'QR_LAOQR_CROSSBANK': return `MERCHANT · ${this.t.crossBankQr}`;
        case 'QR_LDB_TRUST': return `MERCHANT · ${this.t.ldbTrustQr}`;
        default: return type;
      }
    },
    updateHeaders() {
      this.headers = [
        {
          text: this.t.billNumber, value: 'bill_number', width: '120px',
        },
        {
          text: this.t.customer, value: 'customer', width: '150px', sortable: false,
        },
        {
          text: this.t.qrAmount, value: 'qr_amount', width: '120px',
        },
        {
          text: this.t.systemStatus, value: 'qr_status', width: '120px',
        },
        {
          text: this.t.ldbStatus, value: 'ldb_status', width: '110px',
        },
        {
          text: this.t.ldbAmount, value: 'ldb_amount', width: '120px',
        },
        {
          text: this.t.ldbRef, value: 'ldb_payment_ref', width: '130px',
        },
        {
          text: this.t.ldbPayer, value: 'ldb_payer_name', width: '130px',
        },
        {
          text: this.t.paymentDate, value: 'ldb_payment_at', width: '150px',
        },
        {
          text: this.t.matchStatus, value: 'match_status', width: '130px',
        },
        {
          text: this.t.resolution, value: 'resolution_status', width: '120px',
        },
        {
          text: this.t.actions, value: 'actions', sortable: false, width: '180px',
        },
      ];
    },
    updateStmtHeaders() {
      this.stmtHeaders = [
        {
          text: '#', value: 'seq', width: '60px', sortable: false,
        },
        { text: this.t.stmtDate, value: 'statement_date', width: '120px' },
        { text: this.t.transactionTime, value: 'transaction_time', width: '130px' },
        { text: this.t.systemDate, value: 'bt_date', width: '120px' },
        { text: this.t.reference, value: 'transaction_ref', width: '140px' },
        { text: this.t.phone, value: 'customer_phone', width: '120px' },
        { text: this.t.customer, value: 'customer_name', width: '150px' },
        { text: this.t.bankAmount, value: 'amount', width: '130px' },
        { text: this.t.systemAmount, value: 'bt_amount', width: '130px' },
        { text: this.t.billingId, value: 'billing_id', width: '130px' },
        { text: this.t.paymentType, value: 'payment_type', width: '120px' },
        { text: this.t.qrCustomer, value: 'qr_customer_display', width: '180px' },
      ];
    },
    filterByStmtCard(key) {
      this.stmtMatchFilter = key;
    },
    uploadStatement(bank) {
      const file = bank === 'BCEL' ? this.bcelFile : this.ldbFile;
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        // eslint-disable-next-line no-alert
        alert('File size exceeds 10MB limit');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('bank', bank);

      if (bank === 'BCEL') {
        this.uploadingBcel = true;
      } else {
        this.uploadingLdb = true;
      }

      const self = this;
      this.$axios
        .post(`ldb-reconciliation/${this.batchId}/upload-statement`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
        })
        .then((res) => {
          if (bank === 'BCEL') {
            self.uploadingBcel = false;
            self.bcelFile = null;
          } else {
            self.uploadingLdb = false;
            self.ldbFile = null;
          }
          if (res.data.code == 200 || res.data.success) {
            const summary = res.data.data;
            if (summary) {
              self.$set(self.stmtSummary, bank, summary);
              const fileRows = summary.total_rows_in_file || summary.total_rows || 0;
              const filtered = summary.total_rows || 0;
              if (filtered === 0 && fileRows > 0) {
                // eslint-disable-next-line no-alert
                alert(`${bank} Statement: ${Intl.NumberFormat().format(fileRows)} rows in file but 0 rows match the batch date range.\n\nPlease check that the bank statement date matches the reconciliation date.`);
                return;
              }
              const batchDate = self.batch.date_from ? moment(self.batch.date_from).format('YYYY-MM-DD') : '';
              const rowInfo = summary.total_rows_in_file
                ? `${Intl.NumberFormat().format(fileRows)} rows in file, ${Intl.NumberFormat().format(filtered)} in date range${batchDate ? ` (${batchDate})` : ''}`
                : `${Intl.NumberFormat().format(filtered)} rows`;
              const matched = summary.matched_count || 0;
              const missed = summary.missed_payment_count || 0;
              const notInSys = summary.not_in_system_count || 0;
              const expired = summary.expired_count || 0;
              const mismatch = summary.amount_mismatch_count || 0;
              const warnings = (summary.warnings || []).length ? `\n\n\u26A0 ${summary.warnings.join('\n\u26A0 ')}` : '';
              const counts = `\u2713 ${matched} matched | \u26A0 ${missed} missed payments | \u26A0 ${notInSys} not in system | \u25CB ${expired} expired | \u26A1 ${mismatch} mismatch`;
              const msg = `${bank} Statement: ${rowInfo}\n${counts}${warnings}`;
              // eslint-disable-next-line no-alert
              alert(msg);
              self.mainView = 'statement';
              self.bankFilter = bank;
              self.fetchBatch();
              self.fetchStatementRecords();
            }
          }
        })
        .catch((error) => {
          if (bank === 'BCEL') {
            self.uploadingBcel = false;
          } else {
            self.uploadingLdb = false;
          }
          const msg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : `Failed to upload ${bank} statement`;
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    fetchStatementRecords() {
      const params = {};
      if (this.activeSubReconciliationId) {
        params.sub_reconciliation_id = this.activeSubReconciliationId;
      } else if (this.bankFilter) {
        params.bank = this.bankFilter;
      }
      if (this.stmtMatchFilter && this.stmtMatchFilter !== 'all') {
        params.match_status = this.stmtMatchFilter;
      }
      if (this.stmtResolutionFilter && this.stmtResolutionFilter !== 'all') {
        params.resolution_status = this.stmtResolutionFilter;
      }
      if (this.stmtSearch) {
        params.search = this.stmtSearch;
      }
      this.$store.commit('Loading_State', true);
      const self = this;
      this.$axios
        .get(`ldb-reconciliation/${this.batchId}/statement-records`, { params })
        .then((res) => {
          self.$store.commit('Loading_State', false);
          if (res.data.code == 200 || res.data.success) {
            const data = res.data.data;
            let records = [];
            if (Array.isArray(data)) {
              records = data;
            } else if (data && data.records) {
              records = data.records;
              if (data.summary) {
                if (data.summary.BCEL || data.summary.LDB) {
                  self.stmtSummary = data.summary;
                } else {
                  self.$set(self.stmtSummary, self.bankFilter, data.summary);
                }
              }
            } else {
              records = data ? (data.data || []) : [];
            }
            // Add display fields for QR customer and billing IDs
            self.stmtRecords = records.map((r, index) => {
              r.seq = index + 1;
              const qrOwner = r.qr_generator && r.qr_generator.owner;
              const btUser = r.bank_transaction && r.bank_transaction.user;
              if (qrOwner) {
                r.qr_customer_display = `${qrOwner.name || ''}${qrOwner.phone ? ` (${qrOwner.phone})` : ''}`;
              } else {
                r.qr_customer_display = btUser ? (btUser.name || '') : '';
              }
              let ids = [];
              if (r.billing_ids) {
                ids = typeof r.billing_ids === 'string' ? JSON.parse(r.billing_ids) : r.billing_ids;
              }
              r.billing_ids_display = ids.length > 0 ? `${ids.length} ບິນ` : '';
              return r;
            });
          }
        })
        .catch(() => {
          self.$store.commit('Loading_State', false);
          self.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to load statement records',
          });
        });
    },
    fetchBatch() {
      const self = this;
      this.$axios
        .get(`ldb-reconciliation/${this.batchId}`)
        .then((res) => {
          if (res.data.code == 200 || res.data.success) {
            const data = res.data.data;
            if (data.batch) {
              self.batch = { ...self.batch, ...data.batch };
              if (!self.filterDateFrom && data.batch.date_from) {
                self.filterDateFrom = moment(data.batch.date_from).format('YYYY-MM-DD');
              }
              if (!self.filterDateTo && data.batch.date_to) {
                self.filterDateTo = moment(data.batch.date_to).format('YYYY-MM-DD');
              }
              if (data.batch.actual_received_amount && !self.actualReceived) {
                self.actualReceived = String(data.batch.actual_received_amount);
              }
              if (data.batch.comment && !self.settlementComment) {
                self.settlementComment = data.batch.comment;
              }
            }
            if (data.statement_summary) {
              self.stmtSummary = data.statement_summary;
              if (self.stmtSummary.BCEL || self.stmtSummary.LDB) {
                self.mainView = 'statement';
                if (self.stmtSummary.BCEL && !self.stmtSummary.LDB) {
                  self.bankFilter = 'BCEL';
                } else if (!self.stmtSummary.BCEL && self.stmtSummary.LDB) {
                  self.bankFilter = 'LDB';
                }
                self.fetchStatementRecords();
              }
            }
            const records = data.records || (Array.isArray(data) ? data : []);
            if (records.length) {
              self.batch = { ...self.batch, ...self.computeBatchSummary(records) };
            }
          }
        })
        .catch(() => {});
    },
    computeBatchSummary(records) {
      const counts = {
        total_records: records.length,
        matched_count: 0,
        missed_payment_count: 0,
        expired_count: 0,
        amount_mismatch_count: 0,
        ldb_error_count: 0,
        pending_count: 0,
        approved_count: 0,
      };
      records.forEach((r) => {
        if (r.match_status === 'matched') counts.matched_count += 1;
        if (r.match_status === 'missed_payment') counts.missed_payment_count += 1;
        if (r.match_status === 'expired') counts.expired_count += 1;
        if (r.match_status === 'amount_mismatch') counts.amount_mismatch_count += 1;
        if (r.match_status === 'ldb_error') counts.ldb_error_count += 1;
        if (r.resolution_status === 'pending') counts.pending_count += 1;
        if (r.resolution_status === 'approved') counts.approved_count += 1;
      });
      return counts;
    },
    buildParams() {
      const params = {};
      if (this.activeTab && this.activeTab !== 'all') {
        params.match_status = this.activeTab;
      }
      if (this.resolutionFilter && this.resolutionFilter !== 'all') {
        params.resolution_status = this.resolutionFilter;
      }
      if (this.filterDateFrom) {
        params.date_from = this.filterDateFrom;
      }
      if (this.filterDateTo) {
        params.date_to = this.filterDateTo;
      }
      return params;
    },
    applyFilters() {
      this.selectedItems = [];
      this.fetchRecords();
    },
    fetchRecords() {
      this.$store.commit('Loading_State', true);
      const params = this.buildParams();
      const self = this;
      this.$axios
        .get(`ldb-reconciliation/${this.batchId}`, { params })
        .then((res) => {
          self.$store.commit('Loading_State', false);
          if (res.data.code == 200 || res.data.success) {
            const data = res.data.data;
            if (data.batch) {
              self.batch = { ...self.batch, ...data.batch };
            }
            if (data.records) {
              self.records = data.records;
            } else if (Array.isArray(data)) {
              self.records = data;
            } else {
              self.records = data.data || [];
            }
            if (data.pagination) {
              self.pagination = data.pagination;
            }
          }
        })
        .catch(() => {
          self.$store.commit('Loading_State', false);
          self.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to load records',
          });
        });
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '-';
      return `${Intl.NumberFormat().format(amount)} LAK`;
    },
    qrStatusColor(status) {
      if (!status) return 'grey';
      switch (status.toUpperCase()) {
        case 'COMPLETED': return 'green';
        case 'PENDING': return 'yellow darken-2';
        default: return 'grey';
      }
    },
    ldbStatusColor(status) {
      if (!status) return 'grey';
      switch (status.toUpperCase()) {
        case 'FNLD': return 'green';
        case 'EXPD': return 'grey';
        default: return 'blue-grey';
      }
    },
    matchStatusColor(status) {
      if (!status) return 'grey';
      switch (status) {
        case 'matched': return 'green';
        case 'missed_payment': return 'red';
        case 'not_in_system': return 'orange';
        case 'amount_mismatch': return 'amber darken-3';
        case 'expired': return 'grey';
        case 'ldb_error': return 'amber darken-3';
        case 'paid_not_in_statement': return 'blue';
        default: return 'grey';
      }
    },
    formatTransactionTime(val) {
      if (!val) return '-';
      const m = moment.utc(val).utcOffset('+07:00');
      if (!m.isValid()) return val;
      return m.format('HH:mm:ss');
    },
    formatBillingId(val) {
      if (val == null) return '-';
      if (Array.isArray(val)) return val.join(', ');
      return String(val);
    },
    resolutionColor(status) {
      if (!status) return 'grey';
      switch (status) {
        case 'pending': return 'orange';
        case 'approved': return 'green';
        case 'rejected': return 'red';
        case 'not_applicable': return 'grey';
        default: return 'grey';
      }
    },
    formatMatchStatus(status) {
      if (!status) return '-';
      return status.replace(/_/g, ' ');
    },
    formatResolution(status) {
      if (!status) return '-';
      return status.replace(/_/g, ' ');
    },
    rowClass(item) {
      if (item.resolution_status === 'approved') return 'row-approved';
      if (item.resolution_status === 'rejected') return 'row-rejected';
      return '';
    },
    onPerPageChange(val) {
      this.perPage = val;
    },
    filterByTab(key) {
      this.activeTab = key;
    },
    // QR approve/reject
    confirmSingleApprove(item) {
      this.approveIds = [item.id];
      this.approveDate = item.ldb_payment_at
        ? moment(item.ldb_payment_at).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
      this.showApproveDialog = true;
    },
    confirmBulkApprove() {
      this.approveIds = this.selectedItems.map((i) => i.id);
      this.approveDate = moment().format('YYYY-MM-DD');
      this.showApproveDialog = true;
    },
    doApprove() {
      this.actionLoading = true;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/approve', {
          ids: this.approveIds,
          transaction_date: this.approveDate,
        })
        .then((res) => {
          self.actionLoading = false;
          self.showApproveDialog = false;
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Approved successfully',
            });
            self.selectedItems = [];
            self.fetchBatch();
            self.fetchRecords();
          }
        })
        .catch((error) => {
          self.actionLoading = false;
          // e.g. "Only missed_payment rows can be approved (...)" or "The EOD batch ... is closed"
          self.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: (error.response && error.response.data && error.response.data.message) || 'Approve action failed',
          });
        });
    },
    startSingleReject(item) {
      this.rejectIds = [item.id];
      this.rejectReason = '';
      this.showRejectModal = true;
    },
    closeRejectModal() {
      this.showRejectModal = false;
      this.rejectReason = '';
      this.rejectIds = [];
    },
    doReject() {
      if (!this.rejectReason.trim()) return;
      const ids = this.rejectIds.length
        ? this.rejectIds
        : this.selectedItems.map((i) => i.id);
      this.actionLoading = true;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/reject', {
          ids,
          reason: this.rejectReason,
        })
        .then((res) => {
          self.actionLoading = false;
          self.closeRejectModal();
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Rejected successfully',
            });
            self.selectedItems = [];
            self.fetchBatch();
            self.fetchRecords();
          }
        })
        .catch(() => {
          self.actionLoading = false;
          self.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Reject action failed',
          });
        });
    },
    // Statement approve/reject
    confirmSingleApproveStmt(item) {
      // For not_in_system records, always require evidence first
      if (item.match_status === 'not_in_system') {
        this.evidenceRecord = item;
        this.showEvidenceDialog = true;
        return;
      }
      this.approveStmtIds = [item.id];
      this.showApproveStmtDialog = true;
    },
    confirmBulkApproveStmt() {
      // Collect not_in_system records that need evidence
      const needEvidence = this.selectedStmtItems.filter(
        (i) => i.match_status === 'not_in_system',
      );
      if (needEvidence.length > 0) {
        // Queue all not_in_system records for evidence, then approve all selected
        this.bulkEvidenceQueue = [...needEvidence];
        this.bulkApproveIds = this.selectedStmtItems.map((i) => i.id);
        this.processNextEvidence();
        return;
      }
      this.approveStmtIds = this.selectedStmtItems.map((i) => i.id);
      this.showApproveStmtDialog = true;
    },
    processNextEvidence() {
      if (this.bulkEvidenceQueue.length === 0) {
        // All evidence done — show approval confirmation
        this.approveStmtIds = this.bulkApproveIds;
        this.bulkApproveIds = [];
        this.showApproveStmtDialog = true;
        return;
      }
      const next = this.bulkEvidenceQueue.shift();
      this.evidenceRecord = next;
      this.showEvidenceDialog = true;
    },
    doApproveStatement() {
      this.actionLoading = true;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/approve-statement', {
          ids: this.approveStmtIds,
        })
        .then((res) => {
          self.actionLoading = false;
          self.showApproveStmtDialog = false;
          if (res.data.code == 200 || res.data.success) {
            const updated = Array.isArray(res.data.data) ? res.data.data : [];
            const count = updated.filter((r) => r.resolution_status === 'approved').length;
            const unallocated = updated.reduce((sum, r) => sum + Number(r.unallocated_amount || 0), 0);
            // e.g. an in-app row with no bills chosen yet: the server leaves it pending with a note
            const notApproved = updated.filter((r) => r.resolution_status !== 'approved');
            if (notApproved.length > 0) {
              // eslint-disable-next-line no-alert
              alert(`${count} record(s) approved.\n\n${notApproved.map((r) => `${r.transaction_ref}: ${r.notes || ''}`).join('\n')}`);
            } else if (unallocated > 0) {
              // eslint-disable-next-line no-alert
              alert(`${count} record(s) approved.\n${self.t.unallocated}: ${self.fmtLAK(unallocated)}`);
            } else if (count > 0) {
              self.$store.commit('Toast_State', {
                value: true,
                color: 'success',
                msg: `${count} record(s) approved`,
              });
            } else {
              // eslint-disable-next-line no-alert
              alert('No records were updated. The records may have already been processed.');
            }
            self.selectedStmtItems = [];
            self.fetchStatementRecords();
            self.fetchBatch();
          }
        })
        .catch((error) => {
          self.actionLoading = false;
          const msg = error.response && error.response.data
            ? error.response.data.message
            : 'Approve action failed';
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    startSingleRejectStmt(item) {
      this.rejectStmtIds = [item.id];
      this.rejectStmtReason = '';
      this.showRejectStmtModal = true;
    },
    startBulkRejectStmt() {
      this.rejectStmtIds = this.selectedStmtItems.map((i) => i.id);
      this.rejectStmtReason = '';
      this.showRejectStmtModal = true;
    },
    closeRejectStmtModal() {
      this.showRejectStmtModal = false;
      this.rejectStmtReason = '';
      this.rejectStmtIds = [];
    },
    doRejectStatement() {
      if (!this.rejectStmtReason.trim()) return;
      this.actionLoading = true;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/reject-statement', {
          ids: this.rejectStmtIds,
          reason: this.rejectStmtReason,
        })
        .then((res) => {
          self.actionLoading = false;
          self.closeRejectStmtModal();
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Rejected successfully',
            });
            self.selectedStmtItems = [];
            self.fetchStatementRecords();
          }
        })
        .catch(() => {
          self.actionLoading = false;
          self.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Reject action failed',
          });
        });
    },
    enquireQr(item) {
      this.enquiringId = item.id;
      this.enquiringItem = item;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/enquire-qr', {
          statement_record_id: item.id,
          phone: item.customer_phone || item.phone || null,
        }, { timeout: 120000 })
        .then((res) => {
          self.enquiringId = null;
          if (res.data.code == 200 || res.data.success) {
            const matches = res.data.data.matches || [];
            self.qrMatchResults = [{
              statement_record_id: item.id,
              transaction_ref: item.transaction_ref,
              amount: item.amount,
              customer_name: item.customer_name,
              matches,
            }];
            self.showQrMatchModal = true;
          }
        })
        .catch((error) => {
          self.enquiringId = null;
          const msg = error.response && error.response.data
            ? error.response.data.message
            : 'Search failed';
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    bulkEnquireQr() {
      const ids = this.qrSearchableStmtItems.map((i) => i.id);
      if (ids.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No "not in system" items selected');
        return;
      }
      this.enquiringBulk = true;
      this.qrMatchResults = [];
      this.showQrMatchModal = true;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/enquire-qr-bulk', { ids }, { timeout: 300000 })
        .then((res) => {
          self.enquiringBulk = false;
          if (res.data.code == 200 || res.data.success) {
            self.qrMatchResults = res.data.data || [];
          }
        })
        .catch((error) => {
          self.enquiringBulk = false;
          const msg = error.response && error.response.data
            ? error.response.data.message
            : 'Bulk search failed';
          // eslint-disable-next-line no-alert
          alert(msg);
          self.showQrMatchModal = false;
        });
    },
    linkStatementQr(statementRecordId, qrGeneratorId) {
      this.linkingId = qrGeneratorId;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/link-statement-qr', {
          statement_record_id: statementRecordId,
          qr_generator_id: qrGeneratorId,
        })
        .then((res) => {
          self.linkingId = null;
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'QR linked successfully',
            });
            self.showQrMatchModal = false;
            self.fetchStatementRecords();
          }
        })
        .catch((error) => {
          self.linkingId = null;
          const msg = error.response && error.response.data
            ? error.response.data.message
            : 'Link failed';
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    findCustomer(item) {
      this.findCustomerItem = item;
      this.findPhone = item.customer_phone || item.phone || '';
      this.findName = item.customer_name || '';
      this.customerMatchResults = [];
      this.linkBillSelection = {};
      this.showFindCustomerModal = true;
    },
    doFindCustomer() {
      this.findingCustomerId = this.findCustomerItem.id;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/find-customer', {
          statement_record_id: this.findCustomerItem.id,
          phone: this.findPhone || null,
          name: this.findName || null,
        }, { timeout: 30000 })
        .then((res) => {
          self.findingCustomerId = null;
          if (res.data.code == 200 || res.data.success) {
            self.customerMatchResults = [{
              statement_record_id: self.findCustomerItem.id,
              transaction_ref: self.findCustomerItem.transaction_ref,
              amount: self.findCustomerItem.amount,
              matches: res.data.data.matches || [],
            }];
          }
        })
        .catch((error) => {
          self.findingCustomerId = null;
          const msg = error.response && error.response.data ? error.response.data.message : 'Search failed';
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    bulkFindCustomer() {
      const ids = this.selectedStmtItems
        .filter((i) => i.match_status === 'not_in_system')
        .map((i) => i.id);
      if (ids.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No "not in system" items selected');
        return;
      }
      this.findingCustomerBulk = true;
      this.customerMatchResults = [];
      this.linkBillSelection = {};
      this.findCustomerItem = null;
      this.showFindCustomerModal = true;
      const self = this;
      this.$axios
        .post('ldb-reconciliation/find-customer-bulk', { ids }, { timeout: 120000 })
        .then((res) => {
          self.findingCustomerBulk = false;
          if (res.data.code == 200 || res.data.success) {
            self.customerMatchResults = res.data.data || [];
          }
        })
        .catch((error) => {
          self.findingCustomerBulk = false;
          const msg = error.response && error.response.data ? error.response.data.message : 'Bulk search failed';
          // eslint-disable-next-line no-alert
          alert(msg);
          self.showFindCustomerModal = false;
        });
    },
    matchBasisLabel(basis) {
      switch (basis) {
        case 'customer_id': return this.t.basisCustomerId;
        case 'phone': return this.t.basisPhone;
        case 'staff': return this.t.basisStaff;
        default: return basis;
      }
    },
    // Ticked bills of a match; untouched matches start with the bills of the statement's month(s),
    // except amount-only matches, which start with nothing ticked.
    chosenBills(match) {
      if (this.linkBillSelection[match.user_id] !== undefined) return this.linkBillSelection[match.user_id];
      if (match.match_basis === 'amount_only') return [];
      return (match.unpaid_billings || []).filter((b) => b.month_in_statement).map((b) => b.id);
    },
    isBillChosen(match, bill) {
      return this.chosenBills(match).includes(bill.id);
    },
    toggleBill(match, bill) {
      const chosen = this.chosenBills(match);
      this.$set(
        this.linkBillSelection,
        match.user_id,
        chosen.includes(bill.id) ? chosen.filter((id) => id !== bill.id) : [...chosen, bill.id],
      );
    },
    linkStatementCustomer(statementRecordId, userId) {
      this.linkingCustomerId = userId;
      const self = this;
      const match = (this.customerMatchResults || [])
        .flatMap((r) => r.matches || [])
        .find((m) => m.user_id === userId);
      const billingIds = match ? this.chosenBills(match) : [];
      this.$axios
        .post('ldb-reconciliation/link-statement-customer', {
          statement_record_id: statementRecordId,
          user_id: userId,
          // nothing ticked: the server picks (the in-app month bills, else all unpaid ones)
          ...(billingIds.length ? { billing_ids: billingIds } : {}),
        })
        .then((res) => {
          self.linkingCustomerId = null;
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Customer linked successfully',
            });
            self.showFindCustomerModal = false;
            self.fetchStatementRecords();
          }
        })
        .catch((error) => {
          self.linkingCustomerId = null;
          const msg = error.response && error.response.data ? error.response.data.message : 'Link failed';
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    onActualInput(value) {
      this.actualReceived = value.replace(/[^0-9]/g, '');
    },
    saveSettlement() {
      this.savingSettlement = true;
      const self = this;
      this.$axios
        .put(`ldb-reconciliation/${this.batchId}`, {
          actual_received_amount: this.actualReceived || null,
          comment: this.settlementComment || null,
        })
        .then((res) => {
          self.savingSettlement = false;
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Settlement saved',
            });
            self.fetchBatch();
          }
        })
        .catch(() => {
          self.savingSettlement = false;
          self.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to save',
          });
        });
    },
    confirmCloseEod() {
      this.showCloseEodDialog = true;
    },
    doProposeClose() {
      this.proposingClose = true;
      const self = this;
      this.$axios
        .post(`ldb-reconciliation/${this.batchId}/propose-close`, {
          comment: this.proposeComment,
          actual_received_amount: this.actualReceived || null,
        })
        .then((res) => {
          self.proposingClose = false;
          self.showProposeDialog = false;
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Close proposal submitted',
            });
            self.fetchBatch();
          }
        })
        .catch((error) => {
          self.proposingClose = false;
          const msg = error.response && error.response.data ? error.response.data.message : 'Failed';
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    // Mirrors the backend rule (the server still decides): proposer, IT approver and Finance
    // approver are three different people.
    canApproveCloseAs(role) {
      const id = this.currentUserId;
      if (!id) return true;
      const userId = (u) => (u && typeof u === 'object' ? Number(u.id) : Number(u));
      if (userId(this.batch.close_proposed_by) === id) return false;
      const other = role === 'it_manager' ? 'finance_manager' : 'it_manager';
      return !(this.batch[`${other}_approval`] === 'approved' && userId(this.batch[`${other}_approved_by`]) === id);
    },
    closePayload() {
      const payload = { status: 'closed' };
      if (this.actualReceived !== '' && this.actualReceived !== null && this.actualReceived !== undefined) {
        payload.actual_received_amount = Number(this.actualReceived);
      }
      return payload;
    },
    approveClose(role) {
      if (role === 'it_manager') {
        this.approvingIt = true;
      } else {
        this.approvingFinance = true;
      }
      const self = this;
      this.$axios
        .post(`ldb-reconciliation/${this.batchId}/approve-close`, { role })
        .then((res) => {
          self.approvingIt = false;
          self.approvingFinance = false;
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Approved',
            });
            self.fetchBatch();
          }
        })
        .catch((error) => {
          self.approvingIt = false;
          self.approvingFinance = false;
          const msg = error.response && error.response.data ? error.response.data.message : 'Failed';
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    doRejectClose() {
      const self = this;
      this.$axios
        .post(`ldb-reconciliation/${this.batchId}/reject-close`, {
          role: this.rejectRole,
          reason: this.rejectCloseReason,
        })
        .then((res) => {
          self.showRejectCloseDialog = false;
          self.rejectCloseReason = '';
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'info',
              msg: 'Close proposal rejected',
            });
            self.fetchBatch();
          }
        })
        .catch((error) => {
          const msg = error.response && error.response.data ? error.response.data.message : 'Failed';
          // eslint-disable-next-line no-alert
          alert(msg);
        });
    },
    doCloseEod() {
      this.closingBatch = true;
      this.showCloseEodDialog = false;
      const self = this;
      this.$axios
        .put(`ldb-reconciliation/${this.batchId}`, this.closePayload())
        .then((res) => {
          self.closingBatch = false;
          if (res.data.code == 200 || res.data.success) {
            self.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'EOD batch closed',
            });
            self.fetchBatch();
          }
        })
        .catch((error) => {
          self.closingBatch = false;
          self.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: (error.response && error.response.data && error.response.data.message) || 'Failed to close',
          });
        });
    },
    exportBatch() {
      const self = this;
      this.$axios
        .get(`ldb-reconciliation/${this.batchId}/export`)
        .then((res) => {
          if ((res.data.code == 200 || res.data.success) && res.data.data.download_link) {
            window.open(res.data.data.download_link, '_blank');
          }
        })
        .catch(() => {
          self.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to export',
          });
        });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.summary-card {
  transition: all 0.2s ease;
  border-radius: 8px !important;
}
</style>

<style>
.eod-table th {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: #333 !important;
  background-color: #f5f5f5 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  border-bottom: 2px solid #ddd !important;
}
.eod-table td {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #222 !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  border-bottom: 1px solid #eee !important;
}
.eod-table tr:hover td {
  background-color: #f0f7ff !important;
}
.eod-table .row-approved {
  background-color: #f0faf0 !important;
}
.eod-table .row-approved td {
  opacity: 0.7;
}
.eod-table .row-rejected {
  background-color: #fff5f5 !important;
}
.eod-table .row-rejected td {
  opacity: 0.7;
}
.summary-table th {
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  color: #555 !important;
  background-color: #f5f5f5 !important;
}
.summary-table td {
  font-size: 15px !important;
  color: #222 !important;
  font-weight: 500 !important;
  padding: 8px 16px !important;
}
.summary-table .row-green td { background-color: #f0faf0; }
.summary-table .row-red td { background-color: #fff5f5; }
.summary-table .row-orange td { background-color: #fff8f0; }
.summary-table .row-amber td { background-color: #fffbf0; }
.summary-table .row-blue td { background-color: #f0f4ff; }
.summary-table .row-grey td { background-color: #f8f8f8; }
.settlement-detail-table th {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #333 !important;
}
.settlement-detail-table td {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #222 !important;
  padding: 10px 16px !important;
}

/* Dark mode overrides */
.theme--dark .eod-table th {
  color: #ccc !important;
  background-color: #2c2c2c !important;
  border-bottom-color: #444 !important;
}
.theme--dark .eod-table td {
  color: #e0e0e0 !important;
  border-bottom-color: #333 !important;
}
.theme--dark .eod-table tr:hover td {
  background-color: rgba(73, 163, 218, 0.1) !important;
}
.theme--dark .eod-table .row-approved {
  background-color: rgba(76, 175, 80, 0.15) !important;
}
.theme--dark .eod-table .row-rejected {
  background-color: rgba(244, 67, 54, 0.15) !important;
}
.theme--dark .summary-table th {
  color: #ccc !important;
  background-color: #2c2c2c !important;
}
.theme--dark .summary-table td {
  color: #e0e0e0 !important;
}
.theme--dark .summary-table .row-green td {
  background-color: rgba(76, 175, 80, 0.15);
}
.theme--dark .summary-table .row-red td {
  background-color: rgba(244, 67, 54, 0.15);
}
.theme--dark .summary-table .row-orange td {
  background-color: rgba(255, 152, 0, 0.15);
}
.theme--dark .summary-table .row-amber td {
  background-color: rgba(255, 193, 7, 0.15);
}
.theme--dark .summary-table .row-blue td {
  background-color: rgba(33, 150, 243, 0.15);
}
.theme--dark .summary-table .row-grey td {
  background-color: #2a2a2a;
}
.theme--dark .settlement-detail-table th {
  color: #ccc !important;
}
.theme--dark .settlement-detail-table td {
  color: #e0e0e0 !important;
}

/* Settlement section classes */
.settlement-header {
  background-color: #fafafa;
}
.settlement-th-row {
  background: #f5f5f5;
}
.settlement-row-bank {
  background: #e3f2fd;
}
.settlement-row-confirmed {
  background: #e8f5e9;
}
.settlement-row-diff-ok {
  background: #e8f5e9;
  border-top: 2px solid #4caf50;
}
.settlement-row-diff-err {
  background: #ffebee;
  border-top: 2px solid #f44336;
}

/* Dark mode settlement overrides */
.theme--dark .settlement-header {
  background-color: #2c2c2c !important;
}
.theme--dark .settlement-th-row {
  background: #2c2c2c !important;
}
.theme--dark .settlement-row-bank {
  background: rgba(33, 150, 243, 0.15) !important;
}
.theme--dark .settlement-row-confirmed {
  background: rgba(76, 175, 80, 0.15) !important;
}
.theme--dark .settlement-row-diff-ok {
  background: rgba(76, 175, 80, 0.15) !important;
  border-top-color: #4caf50;
}
.theme--dark .settlement-row-diff-err {
  background: rgba(244, 67, 54, 0.15) !important;
  border-top-color: #f44336;
}

/* Lighten Vuetify text color utilities in dark mode for settlement table */
.theme--dark .settlement-detail-table td.blue--text.text--darken-2 {
  color: #90caf9 !important;
}
.theme--dark .settlement-detail-table td.purple--text.text--darken-1 {
  color: #e1bee7 !important;
}
.theme--dark .settlement-detail-table td.green--text.text--darken-2 {
  color: #a5d6a7 !important;
}
.theme--dark .settlement-detail-table td.red--text {
  color: #ef9a9a !important;
}
.theme--dark .settlement-detail-table td.green--text {
  color: #a5d6a7 !important;
}

/* Comprehensive dark mode: tables, cards, backgrounds */
.bg-light-grey {
  background: #f5f5f5;
  border-radius: 4px;
}
.theme--dark .bg-light-grey {
  background: #333 !important;
}
.theme--dark .v-data-table {
  background-color: #1e1e1e !important;
}
.theme--dark .v-data-table th {
  background-color: #2c2c2c !important;
  color: #ccc !important;
}
.theme--dark .v-data-table td {
  color: #e0e0e0 !important;
}
.theme--dark .v-data-table .v-data-footer {
  color: #bbb !important;
}
.theme--dark .summary-table .v-data-table__wrapper,
.theme--dark .v-simple-table .v-data-table__wrapper {
  background-color: transparent !important;
}
.theme--dark .v-simple-table {
  background-color: transparent !important;
}
.theme--dark .v-data-table .v-data-table__empty-wrapper {
  color: #999 !important;
}
.theme--dark .v-data-table tr {
  background-color: transparent !important;
}
.theme--dark .v-data-table .v-data-table__wrapper {
  background-color: transparent !important;
}
.theme--dark .v-data-footer {
  border-top-color: #333 !important;
}
.theme--dark .v-card.v-card--outlined {
  border-color: #444 !important;
}
</style>
